// services/database.js - Refactored
import { supabase, handleSupabaseError } from '../lib/supabase';

// ========================================
// 🛠️ COMMON HELPERS
// ========================================

// Smart Request Deduplication System
const pendingRequests = new Map();
const requestResults = new Map();
const realtimeInvalidations = new Set();
const REQUEST_CACHE_DURATION = 2000; // 2 seconds
const REQUEST_TIMEOUT = 10000; // 10 seconds timeout for user operations

// Operations that should never be deduplicated (write operations)
const NEVER_DEDUPLICATE = [
  'createTournament',
  'updateTournament', 
  'deleteTournament',
  'deleteTournamentSimple',
  'createTeam',
  'updateTeam',
  'deleteTeam',
  'submitMatchResult',
  'submitTournamentMatchResult',
  'approveTournamentSubmission',
  'rejectTournamentSubmission',
  'recordMatchResult',
  'recordGameResult',
  'updateUserProfile',
  'inviteToTeam',
  'respondToInvitation',
  'registerTeamForTournament',
  'removeTeamFromTournament',
  'promoteToTeamCaptain',
  'demoteFromTeamCaptain',
  'transferTeamOwnership',
  'canSubmitTournamentResult'  // Never deduplicate permission checks
];

// Helper to identify read operations
const isReadOperation = (operationName) => {
  const readPrefixes = ['get', 'fetch', 'load', 'search', 'find', 'check'];
  return readPrefixes.some(prefix => 
    operationName.toLowerCase().startsWith(prefix)
  );
};

// Smart filtering for deduplication
const shouldDeduplicate = (operationName, options = {}) => {
  // Force deduplication if explicitly requested
  if (options.forceDeduplication) return true;
  
  // Never deduplicate if explicitly disabled
  if (options.disableDeduplication) return false;
  
  // Never deduplicate operations in the blocklist
  if (NEVER_DEDUPLICATE.includes(operationName)) return false;
  
  // Deduplicate read operations by default
  return isReadOperation(operationName);
};

// Helper to create a cache key for requests
const createRequestKey = (operationName, ...args) => {
  return `${operationName}:${JSON.stringify(args)}`;
};

// Cache invalidation for real-time updates
const invalidateCache = (pattern) => {
  const keysToDelete = [];
  
  for (const key of requestResults.keys()) {
    if (key.includes(pattern)) {
      keysToDelete.push(key);
      realtimeInvalidations.add(key);
    }
  }
  
  keysToDelete.forEach(key => {
    requestResults.delete(key);
    pendingRequests.delete(key);
  });
};

// Force clear all hanging requests (emergency cleanup)
const clearHangingRequests = () => {
  console.log('🧹 Clearing hanging requests:', pendingRequests.size, 'pending,', requestResults.size, 'cached');
  pendingRequests.clear();
  requestResults.clear();
  realtimeInvalidations.clear();
};

// Export for emergency use
window.clearHangingRequests = clearHangingRequests;

// Clear any existing hanging requests immediately
clearHangingRequests();

// Timeout wrapper to prevent hanging requests
const withTimeout = (promise, timeoutMs = REQUEST_TIMEOUT, operationName = 'request') => {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Request timeout: ${operationName} took longer than ${timeoutMs}ms`));
      }, timeoutMs);
      
      // Clean up timeout if promise resolves first
      promise.finally(() => clearTimeout(timeoutId));
    })
  ]);
};

// Enhanced request deduplication with smart filtering and real-time support
const deduplicatedRequest = async (operationName, args, operation, options = {}) => {
  // Skip deduplication for operations that shouldn't be deduplicated
  if (!shouldDeduplicate(operationName, options)) {
    return operation();
  }

  const key = createRequestKey(operationName, ...args);
  
  const { 
    bypassCache = false,           // For manual refresh
    invalidateOnRealtime = true,   // For real-time updates  
    cacheDuration = REQUEST_CACHE_DURATION, // 2 second default cache
    timeout = REQUEST_TIMEOUT      // Request timeout in milliseconds
  } = options;
  
  // Check if real-time invalidated this cache
  if (invalidateOnRealtime && realtimeInvalidations.has(key)) {
    requestResults.delete(key);
    realtimeInvalidations.delete(key);
  }
  
  // Bypass cache for manual refresh
  if (bypassCache) {
    requestResults.delete(key);
    pendingRequests.delete(key);
  }
  
  // Check cache first
  const cached = requestResults.get(key);
  const now = Date.now();
  if (cached) {
    const age = now - cached.timestamp;
    if (age < cacheDuration) {
      return cached.result;
    }
    // Remove stale cache entry
    requestResults.delete(key);
  }
  
  // Check if already pending - use atomic check and cleanup
  const existingPending = pendingRequests.get(key);
  if (existingPending) {
    try {
      return await existingPending;
    } catch (error) {
      // If the pending request failed, clean it up and retry once
      pendingRequests.delete(key);
      // Don't retry immediately to avoid infinite recursion
      throw error;
    }
  }
  
  // Execute fresh request with timeout protection
  const promise = (async () => {
    try {
      const result = await withTimeout(operation(), timeout, operationName);
      
      // Cache the successful result
      requestResults.set(key, {
        result,
        timestamp: Date.now()
      });
      
      return result;
    } finally {
      // Always clean up pending request
      pendingRequests.delete(key);
    }
  })();
  
  pendingRequests.set(key, promise);
  return promise;
};

// Export invalidateCache for use in contexts
window.invalidateCache = invalidateCache;

// Cleanup stale cache entries every 30 seconds
setInterval(() => {
  const now = Date.now();

  for (const [key, cached] of requestResults.entries()) {
    if (now - cached.timestamp > REQUEST_CACHE_DURATION * 2) {
      requestResults.delete(key);
    }
  }

}, 30000);

/**
 * Enhanced wrapper for database operations with smart deduplication and error handling
 */
export const dbOperation = async (operation, context, options = {}) => {
  // Apply smart deduplication to appropriate operations
  if (shouldDeduplicate(context, options)) {
    // Extract cache args from options for parameter-specific caching
    const cacheArgs = options.cacheArgs || [];
    return deduplicatedRequest(context, cacheArgs, async () => {
      try {
        return await operation();
      } catch (error) {
        return handleSupabaseError(error, context);
      }
    }, options);
  }
  
  // Direct execution for write operations
  try {
    return await operation();
  } catch (error) {
    return handleSupabaseError(error, context);
  }
};

/**
 * Simplified tournament deletion that works with RLS policies
 * This version focuses on working within existing RLS constraints
 */
export const deleteTournamentSimple = async (tournamentId, adminId) => {
  return dbOperation(async () => {
    
    await verifyAdmin(adminId);
    
    // Get tournament info
    const { data: tournament, error: tournamentError } = await supabase
      .from('tournaments')
      .select('id, name, status, bracket_generated, created_by')
      .eq('id', tournamentId)
      .single();

    if (tournamentError || !tournament) {
      throw new Error('Tournament not found');
    }

    // Check permissions (admin or tournament creator)
    const currentUser = await supabase.auth.getUser();
    const isCreator = tournament.created_by === adminId;
    const isAdmin = currentUser.data.user?.role === 'admin';
    
    if (!isCreator && !isAdmin) {
      throw new Error('Insufficient permissions to delete this tournament');
    }

    // Use a transaction-like approach with careful ordering
    try {
      // 1. Get match IDs first
      const { data: matches } = await supabase
        .from('matches')
        .select('id')
        .eq('tournament_id', tournamentId);
      
      const matchIds = matches?.map(m => m.id) || [];
      
      // 2. Delete in correct order for foreign key constraints
      if (matchIds.length > 0) {
        // Delete player stats
        await supabase
          .from('player_match_stats')
          .delete()
          .in('match_id', matchIds);
        
        // Note: match_games table no longer exists - games are now in unified matches table
        
        // Delete matches
        await supabase
          .from('matches')
          .delete()
          .eq('tournament_id', tournamentId);
      }
      
      // Delete group assignments
      await supabase
        .from('team_group_assignments')
        .delete()
        .eq('tournament_id', tournamentId);
      
      // Delete groups
      await supabase
        .from('tournament_groups')
        .delete()
        .eq('tournament_id', tournamentId);
      
      // Delete registrations
      await supabase
        .from('tournament_registrations')
        .delete()
        .eq('tournament_id', tournamentId);
      
      // Finally delete tournament
      const { error: deleteError, count } = await supabase
        .from('tournaments')
        .delete({ count: 'exact' })
        .eq('id', tournamentId)
        .eq('created_by', adminId); // Ensure only creator can delete
      
      if (deleteError) {
        throw deleteError;
      }
      
      if (count === 0) {
        throw new Error('Tournament not deleted - you may not be the creator');
      }
      
      console.log('Tournament deleted successfully');
      
      return {
        success: true,
        data: {
          tournamentId,
          tournamentName: tournament.name,
          wasStarted: tournament.bracket_generated,
          deletedAt: new Date().toISOString()
        }
      };
      
    } catch (error) {
      console.error('Deletion failed:', error);
      throw error;
    }
  }, 'deleting tournament (simple)');
};

/**
 * Format tournament format from display format to database format
 */
const formatForDatabase = (displayFormat) => {
  const formatMap = {
    'single_elimination': 'single_elimination',
    'Single Elimination': 'single_elimination',
    'round_robin': 'round_robin', 
    'Round Robin': 'round_robin',
    'Best of 3': 'bo3',
    'Best of 5': 'bo5',
    'Bo3': 'bo3',
    'Bo5': 'bo5',
    'bo3': 'bo3',
    'bo5': 'bo5',
    'League': 'league',
    'league': 'league',
    'Versus': 'versus',
    'versus': 'versus',
    'Group Stage + Knockout': 'group_stage_knockout',
    'group_stage_knockout': 'group_stage_knockout'
  };
  return formatMap[displayFormat] || displayFormat.toLowerCase();
};

/**
 * Verify admin permissions
 */
const verifyAdmin = async (userId) => {
  const { data, error } = await supabase
  .from('users')
  .select('role')
  .eq('id', userId)
  .single();

  if (error) throw error;
  if (data.role !== 'admin') {
    throw new Error('Only administrators can perform this action');
  }
  return data;
};

/**
 * Verify tournament management permissions (admin, creator, or owner)
 */
export const verifyTournamentManagement = async (userId, tournamentId) => {
  // Check if user is global admin
  const { data: userData, error: userError } = await supabase
    .from('users')
    .select('role')
    .eq('id', userId)
    .single();

  if (userError) {
    if (userError.code === 'PGRST116') {
      throw new Error('User not found');
    }
    throw userError;
  }
  
  // Global admins can manage any tournament
  if (userData.role === 'admin') {
    return { isAdmin: true, isCreator: false, isOwner: false };
  }

  // Check if user is tournament creator or owner
  const { data: tournamentData, error: tournamentError } = await supabase
    .from('tournaments')
    .select('created_by')
    .eq('id', tournamentId)
    .single();

  if (tournamentError) {
    if (tournamentError.code === 'PGRST116') {
      throw new Error('Tournament not found');
    }
    throw tournamentError;
  }

  const isCreator = tournamentData.created_by === userId;
  
  // Check if user is tournament owner
  const { data: ownerData, error: ownerError } = await supabase
    .from('tournament_owners')
    .select('id')
    .eq('tournament_id', tournamentId)
    .eq('user_id', userId)
    .limit(1);

  if (ownerError) throw ownerError;
  
  const isOwner = ownerData && ownerData.length > 0;

  if (!isCreator && !isOwner) {
    throw new Error('You do not have permission to manage this tournament');
  }

  return { isAdmin: false, isCreator, isOwner };
};

/**
 * Verify user is a member of either participating team
 */
const verifyTeamMembership = async (userId, team1Id, team2Id) => {
  
  const { data: membershipData, error: membershipError } = await supabase
    .from('team_members')
    .select('team_id')
    .eq('user_id', userId)
    .in('team_id', [team1Id, team2Id])
    .limit(1);

  if (membershipError) {
    console.error('❌ Team membership error:', membershipError);
    throw membershipError;
  }
  
  console.log('📊 Team membership data:', membershipData);
  
  if (!membershipData || membershipData.length === 0) {
    console.error('❌ User not member of either team');
    throw new Error('You do not have permission to manage this tournament');
  }

  return { isTeamMember: true, teamId: membershipData[0].team_id };
};

/**
 * Check team ownership
 */
const verifyTeamOwner = async (teamId, userId) => {
  const { data, error } = await supabase
  .from('teams')
  .select('owner_id')
  .eq('id', teamId)
  .single();

  if (error) throw error;
  if (data.owner_id !== userId) {
    throw new Error('Only team owners can perform this action');
  }
  return data;
};

/**
 * Get user by username or display name
 */
const findUser = async (identifier) => {
  const { data, error } = await supabase
  .from('users')
  .select('id, username, display_name')
  .or(`username.ilike.${identifier},display_name.ilike.${identifier}`)
  .single();

  if (error || !data) throw new Error('User not found');
  return data;
};

/**
 * Common team query with members
 */
const teamWithMembersQuery = () => `
  *,
  owner:users(username, display_name, avatar_url),
  members:team_members(
    user:users(id, username, display_name, avatar_url),
    role,
    joined_at
  )
`;

/**
 * Common tournament query with registrations
 */
const tournamentWithRegistrationsQuery = () => `
  *,
  require_schedule_approval,
  official_streams,
  created_by:users(username, avatar_url),
  tournament_registrations(
    team:teams(
      id, name, logo, description, country_code,
      owner:users(id, username, display_name, avatar_url),
      members:team_members(
        user_id,
        role,
        user:users(id, username, display_name, avatar_url)
      )
    )
  )
`;

/**
 * Common match query with teams and stats
 */
const matchWithDetailsQuery = () => `
  *,
  team1:teams!matches_team1_id_fkey(
    id, name, logo,
    members:team_members(
      user:users(id, username, display_name, avatar_url, gamer_tag)
    )
  ),
  team2:teams!matches_team2_id_fkey(
    id, name, logo,
    members:team_members(
      user:users(id, username, display_name, avatar_url, gamer_tag)
    )
  ),
  winner:teams!matches_winner_id_fkey(id, name, logo),
  series_winner:teams!matches_series_winner_id_fkey(id, name, logo),
  player_stats:player_match_stats(
    *,
    player:users(id, username, display_name, avatar_url)
  ),
  tournament_group:tournament_groups(id, group_name, group_number)
`;

// ========================================
// 🔐 USER FUNCTIONS
// ========================================

export const updateUserProfile = async (userId, profileData) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .from('users')
    .update(profileData)
    .eq('id', userId)
    .select()
    .single();

    if (error) throw error;
    return { success: true, data };
  }, 'updating user profile');
};

export const searchUsers = async (searchTerm, limit = 10) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .from('users')
    .select('id, username, display_name, avatar_url')
    .or(`username.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%`)
    .limit(limit);

    if (error) throw error;
    return { success: true, data };
  }, 'searching users');
};

/**
 * Get a player's recent match history with their per-match stats,
 * opponent, tournament, result and MVP flag.
 */
export const getUserMatchHistory = async (playerId, limit = 8) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .from('player_match_stats')
      .select(`
        id, team_id, goals, assists, passes, interceptions, saves,
        match:matches!inner(
          id, team1_id, team2_id, team1_score, team2_score, winner_id, status,
          match_date, created_at, series_type, mvp_player_id,
          tournament:tournaments(id, name, logo),
          team1:teams!matches_team1_id_fkey(id, name, logo, country_code),
          team2:teams!matches_team2_id_fkey(id, name, logo, country_code)
        )
      `)
      .eq('player_id', playerId)
      .eq('match.status', 'completed')
      .order('created_at', { ascending: false })
      .limit(300);

    if (error) throw error;

    const rows = (data || [])
      .filter(r => r.match)
      .map(r => {
        const m = r.match;
        const playerTeamId = r.team_id;
        const isTeam1 = m.team1_id === playerTeamId;
        const opponent = isTeam1 ? m.team2 : m.team1;
        const playerScore = (isTeam1 ? m.team1_score : m.team2_score) ?? 0;
        const oppScore = (isTeam1 ? m.team2_score : m.team1_score) ?? 0;
        return {
          id: r.id,
          matchId: m.id,
          date: m.match_date || m.created_at,
          tournament: m.tournament || null,
          seriesType: m.series_type || null,
          opponent: opponent || null,
          won: m.winner_id === playerTeamId,
          isDraw: !m.winner_id,
          isMvp: m.mvp_player_id === playerId,
          playerScore,
          oppScore,
          stats: {
            goals: r.goals || 0,
            assists: r.assists || 0,
            passes: r.passes || 0,
            interceptions: r.interceptions || 0,
            saves: r.saves || 0
          }
        };
      })
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
      .slice(0, limit);

    return { success: true, data: rows };
  }, 'fetching user match history');
};

export const getUserProfile = async (userId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .from('users')
    .select(`
      id, username, display_name, avatar_url, bio, platform, 
      gamer_tag, position, jersey_number, country_code, created_at
    `)
    .eq('id', userId)
    .single();

    if (error) throw error;
    return { success: true, data };
  }, 'fetching user profile');
};

// Simple scrim leaderboard functions for dashboard widget

export const getUserStats = async (userId, scrimGameMode = '3v3') => {
  return dbOperation(async () => {
    // Use enhanced RPC function to get player stats with all fields including total_wins
    const { data: playerStats, error: statsError } = await supabase
      .rpc('get_player_leaderboards')
      .eq('player_id', userId)
      .single();

    if (statsError) throw statsError;

    // Get tournament wins from tournament winners with tournament details
    const { data: tournamentWins, error: tournamentWinsError } = await supabase
      .rpc('get_tournament_winners');

    let totalTournamentWins = 0;
    let userTournamentWins = [];
    if (!tournamentWinsError && tournamentWins) {
      // Get user's teams to check tournament wins
      const { data: userTeams, error: userTeamsError } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('user_id', userId);

      if (!userTeamsError && userTeams) {
        const userTeamIds = userTeams.map(t => t.team_id);
        const userWins = tournamentWins.filter(win => 
          userTeamIds.includes(win.winner_team_id)
        );
        totalTournamentWins = userWins.length;

        // Get detailed tournament information for each win
        if (userWins.length > 0) {
          const tournamentIds = userWins.map(win => win.tournament_id);
          const { data: tournaments, error: tournamentsError } = await supabase
            .from('tournaments')
            .select('id, name, logo, format, prize_pool, end_date, created_at')
            .in('id', tournamentIds)
            .neq('status', 'hide');

          // Get team details for the winning teams
          const winnerTeamIds = userWins.map(win => win.winner_team_id);
          const { data: winnerTeams, error: teamsError } = await supabase
            .from('teams')
            .select('id, name, logo')
            .in('id', winnerTeamIds);

          if (!tournamentsError && tournaments && !teamsError && winnerTeams) {
            userTournamentWins = userWins.map(win => {
              const tournament = tournaments.find(t => t.id === win.tournament_id);
              const team = winnerTeams.find(t => t.id === win.winner_team_id);
              return {
                ...win,
                tournament,
                team
              };
            }).filter(win => win.tournament) // Exclude wins where tournament is null (hidden)
            .sort((a, b) => new Date(b.tournament?.end_date || b.tournament?.created_at) - new Date(a.tournament?.end_date || a.tournament?.created_at));
          }
        }
      }
    }

    // Get user's teams with full stats
    const { data: teamMembers, error: teamsError } = await supabase
      .from('team_members')
      .select(`
        role,
        team:teams(id, name, logo, team_members(count))
      `)
      .eq('user_id', userId);

    if (teamsError) throw teamsError;

    // Count this player's own games per team (from per-match stats)
    const { data: playerTeamGames } = await supabase
      .from('player_match_stats')
      .select('team_id')
      .eq('player_id', userId);
    const playerGamesByTeam = {};
    (playerTeamGames || []).forEach(r => {
      if (r.team_id) playerGamesByTeam[r.team_id] = (playerGamesByTeam[r.team_id] || 0) + 1;
    });

    const teamsWithStats = teamMembers ? await Promise.all(
      teamMembers.map(async (tm) => {
        const team = tm.team;
        if (!team) return null;

        // Fetch tournament stats
        const { data: tournamentStats } = await supabase
          .rpc('get_team_leaderboards')
          .eq('team_id', team.id)
          .single();

        // Fetch scrim stats
        const { data: scrimStats } = await supabase
          .rpc('get_team_scrim_stats', { team_id_param: team.id })
          .single();
        
        const totalTournamentMatches = tournamentStats?.total_matches || 0;
        const totalTournamentWins = tournamentStats?.match_wins || 0;
        
        const totalScrimMatches = scrimStats?.total_matches || 0;
        const totalScrimWins = scrimStats?.total_wins || 0;

        const totalMatches = totalTournamentMatches + totalScrimMatches;
        const totalWins = totalTournamentWins + totalScrimWins;
        const totalLosses = totalMatches - totalWins;

        return {
          ...team,
          role: tm.role,
          memberCount: team.team_members[0]?.count || 0,
          gamesPlayed: totalMatches,
          playerGamesPlayed: playerGamesByTeam[team.id] || 0,
          stats: {
            wins: totalWins,
            losses: totalLosses,
            gamesPlayed: totalMatches,
          }
        };
      })
    ) : [];

    const finalTeams = teamsWithStats.filter(Boolean).sort((a, b) => b.gamesPlayed - a.gamesPlayed);

    // Get rankings using the same functions as leaderboards
    const [goalsLeaderboard, assistsLeaderboard, passesLeaderboard, interceptionsLeaderboard, savesLeaderboard] = await Promise.all([
      getTopGoalScorers(1000),
      getTopAssistLeaders(1000),
      getTopPassLeaders(1000),
      getTopInterceptionLeaders(1000),
      getTopSaveLeaders(1000)
    ]);

    // Find user's rank in each category
    const goalsRank = goalsLeaderboard.data?.findIndex(p => p.player_id === userId) + 1 || null;
    const assistsRank = assistsLeaderboard.data?.findIndex(p => p.player_id === userId) + 1 || null;
    const passesRank = passesLeaderboard.data?.findIndex(p => p.player_id === userId) + 1 || null;
    const interceptionsRank = interceptionsLeaderboard.data?.findIndex(p => p.player_id === userId) + 1 || null;
    const savesRank = savesLeaderboard.data?.findIndex(p => p.player_id === userId) + 1 || null;

    // Get scrim statistics
    let scrimStats = {
      scrimMatches: 0,
      scrimWins: 0,
      scrimLosses: 0,
      scrimWinRate: 0,
      scrimGoals: 0,
      scrimAssists: 0,
      scrimSaves: 0,
      scrimPasses: 0,
      scrimInterceptions: 0
    };

    try {
      // Get player's scrim match history for both game modes to calculate combined stats
      const [scrimHistory3v3, scrimHistory5v5] = await Promise.all([
        getPlayerScrimHistory(userId, 100, '3v3'),
        getPlayerScrimHistory(userId, 100, '5v5')
      ]);
      
      // Combine both histories
      const allScrimHistory = [...(scrimHistory3v3 || []), ...(scrimHistory5v5 || [])];
      
      if (allScrimHistory && allScrimHistory.length > 0) {
        const completedMatches = allScrimHistory.filter(entry => entry.match?.status === 'completed');
        
        scrimStats.scrimMatches = completedMatches.length;
        
        // Calculate totals and win/loss
        const totals = completedMatches.reduce((acc, entry) => {
          // Determine if player won
          let playerWon = false;
          if (entry.match?.winner_team) {
            if (entry.team_number === 1 && entry.match.team1_score > entry.match.team2_score) {
              playerWon = true;
            } else if (entry.team_number === 2 && entry.match.team2_score > entry.match.team1_score) {
              playerWon = true;
            }
          }
          
          return {
            wins: acc.wins + (playerWon ? 1 : 0),
            goals: acc.goals + (entry.goals || 0),
            assists: acc.assists + (entry.assists || 0),
            saves: acc.saves + (entry.saves || 0),
            passes: acc.passes + (entry.passes || 0),
            interceptions: acc.interceptions + (entry.interceptions || 0)
          };
        }, { wins: 0, goals: 0, assists: 0, saves: 0, passes: 0, interceptions: 0 });
        
        scrimStats.scrimWins = totals.wins;
        scrimStats.scrimLosses = scrimStats.scrimMatches - totals.wins;
        scrimStats.scrimWinRate = scrimStats.scrimMatches > 0 ? Math.round((totals.wins / scrimStats.scrimMatches) * 100) : 0;
        scrimStats.scrimGoals = totals.goals;
        scrimStats.scrimAssists = totals.assists;
        scrimStats.scrimSaves = totals.saves;
        scrimStats.scrimPasses = totals.passes;
        scrimStats.scrimInterceptions = totals.interceptions;
      }
    } catch (error) {
      console.error('Error fetching scrim stats:', error);
      // Keep default values on error
    }

    // Get MVP statistics
    let mvpStats = {
      totalMVPs: 0,
      mvpRank: null
    };
    
    try {
      // Get player's MVP stats
      const { data: mvpData, error: mvpError } = await supabase
        .rpc('get_player_mvp_stats', { player_id_param: userId });

      if (!mvpError && mvpData?.[0]) {
        mvpStats.totalMVPs = parseInt(mvpData[0].total_mvps) || 0;
      }

      // Get MVP rank by checking global MVP leaderboard
      if (mvpStats.totalMVPs > 0) {
        const { data: mvpLeaderboard, error: leaderboardError } = await supabase
          .rpc('get_global_mvp_leaderboard', { limit_param: 1000 });

        if (!leaderboardError && mvpLeaderboard) {
          // Find user's position and calculate proper rank accounting for ties
          let currentRank = 1;
          let previousMvpCount = null;
          let previousMvpPercentage = null;
          
          for (let i = 0; i < mvpLeaderboard.length; i++) {
            const entry = mvpLeaderboard[i];
            const mvpCount = parseInt(entry.mvp_count) || 0;
            const mvpPercentage = parseFloat(entry.mvp_percentage) || 0;
            
            // If this entry has different stats than the previous one, update rank
            if (previousMvpCount !== null && 
                (mvpCount !== previousMvpCount || mvpPercentage !== previousMvpPercentage)) {
              currentRank = i + 1;
            }
            
            if (entry.player_id === userId) {
              mvpStats.mvpRank = currentRank;
              break;
            }
            
            previousMvpCount = mvpCount;
            previousMvpPercentage = mvpPercentage;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching MVP stats:', error);
      // Keep default values on error
    }

    // Use data from enhanced RPC function that now includes all fields
    const stats = {
      // Tournament statistics (existing)
      totalGoals: playerStats?.total_goals || 0,
      totalAssists: playerStats?.total_assists || 0,
      totalPasses: playerStats?.total_passes || 0,
      totalInterceptions: playerStats?.total_interceptions || 0,
      totalSaves: playerStats?.total_saves || 0,
      totalMatches: playerStats?.total_matches || 0,
      totalMatchesPlayed: playerStats?.total_matches || 0,
      totalMatchWins: playerStats?.total_wins || 0,
      totalTournamentWins,
      tournamentWins: userTournamentWins,
      wins: playerStats?.total_wins || 0,
      losses: (playerStats?.total_matches || 0) - (playerStats?.total_wins || 0),
      tournamentsWon: totalTournamentWins,
      tournamentsPlayed: 0, // This would need a separate calculation if needed
      goalsRank: goalsRank > 0 ? goalsRank : null,
      assistsRank: assistsRank > 0 ? assistsRank : null,
      passesRank: passesRank > 0 ? passesRank : null,
      interceptionsRank: interceptionsRank > 0 ? interceptionsRank : null,
      savesRank: savesRank > 0 ? savesRank : null,
      teams: finalTeams,
      // MVP statistics
      totalMVPs: mvpStats.totalMVPs,
      mvpRank: mvpStats.mvpRank,
      // Scrim statistics (new)
      ...scrimStats
    };

    return { success: true, data: stats };
  }, 'fetching user stats');
};

// ========================================
// 🏆 TOURNAMENT FUNCTIONS
// ========================================

export const getTournaments = async () => {
  return dbOperation(async () => {
    const { data: tournaments, error } = await supabase
    .from('tournaments')
    .select(tournamentWithRegistrationsQuery())
    .order('created_at', { ascending: false });

    if (error) throw error;

    // Get winners for completed tournaments using unified backend function
    const { data: allWinners } = await supabase.rpc('get_tournament_winners');
    const winnersMap = new Map(allWinners?.map(w => [w.tournament_id, w]) || []);

    // Add winners to tournaments
    const tournamentsWithWinners = await Promise.all(
      tournaments.map(async (tournament) => {
        if (tournament.status !== 'completed') {
          return tournament;
        }

        const winnerData = winnersMap.get(tournament.id);
        if (winnerData) {
          // Get team details for the winner
          const { data: winnerTeam } = await supabase
            .from('teams')
            .select('id, name, logo')
            .eq('id', winnerData.winner_team_id)
            .single();

          if (winnerTeam) {
            return {
              ...tournament,
              winner: winnerTeam
            };
          }
        }

        return tournament;
      })
    );

    return { success: true, data: tournamentsWithWinners };
  }, 'fetching tournaments');
};

export const createTournament = async (tournamentData, userId) => {
  return dbOperation(async () => {
    await verifyAdmin(userId);
    
    // Server-side validation for team counts and format requirements
    const format = formatForDatabase(tournamentData.format);
    const minTeams = parseInt(tournamentData.minTeams) || 4;
    const maxTeams = parseInt(tournamentData.maxTeams) || 16;
    
    // Basic team count validation
    if (minTeams < 2) {
      throw new Error('Minimum teams must be at least 2');
    }
    if (minTeams > maxTeams) {
      throw new Error('Minimum teams cannot exceed maximum teams');
    }
    
    // Power of 2 validation for specific formats
    if (['single_elimination', 'bo3', 'bo5'].includes(format)) {
      if (minTeams < 4) {
        throw new Error('Single Elimination, BO3, and BO5 tournaments require at least 4 teams');
      }
      
      // Check if minTeams is power of 2
      if (!Number.isInteger(Math.log2(minTeams))) {
        throw new Error('Single Elimination, BO3, and BO5 tournaments require minimum teams to be a power of 2 (4, 8, 16, 32, 64...)');
      }
      
      // Check if maxTeams is power of 2
      if (!Number.isInteger(Math.log2(maxTeams))) {
        throw new Error('Single Elimination, BO3, and BO5 tournaments require maximum teams to be a power of 2 (4, 8, 16, 32, 64...)');
      }
    }

    // Handle timezone-aware datetime for tournament start
    let startDatetime = null;
    let schedulerTimezone = null;
    
    if (tournamentData.startDate && tournamentData.startTime) {
      // Parse the date and time components
      const [year, month, day] = tournamentData.startDate.split('-').map(Number);
      const [hours, minutes] = tournamentData.startTime.split(':').map(Number);
      
      // Create date using individual components to ensure local timezone
      const localDateTime = new Date(year, month - 1, day, hours, minutes, 0);
      
      // Convert to ISO string (UTC) - this handles DST automatically
      startDatetime = localDateTime.toISOString();
      schedulerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      console.log('Creating tournament with timezone support:', {
        input: `${tournamentData.startDate} ${tournamentData.startTime}`,
        localDateTime: localDateTime.toString(),
        utc: startDatetime,
        timezone: schedulerTimezone
      });
    }

    // Prepare tournament data with journey system fields
    const dbTournamentData = {
      name: tournamentData.name,
      description: tournamentData.description,
      format: formatForDatabase(tournamentData.format),
      platform: tournamentData.platform || 'all',
      max_teams: tournamentData.maxTeams || 16,
      min_teams: tournamentData.minTeams || 4,
      prize_pool: tournamentData.prize,
      start_date: tournamentData.startDate,
      start_time: tournamentData.startTime,
      // New timezone-aware fields
      start_datetime: startDatetime,
      scheduler_timezone: schedulerTimezone,
      end_date: tournamentData.endDate || null,
      registration_deadline: tournamentData.registrationDeadline || null,
      group_assignment_type: tournamentData.groupAssignmentType || 'random',
      games_per_matchup: tournamentData.gamesPerMatchup || 1,
      group_a_name: tournamentData.groupAName || 'Group A',
      group_b_name: tournamentData.groupBName || 'Group B',
      // Journey system fields
      journey_duration: tournamentData.journeyDuration || '1_day',
      tournament_size_format: tournamentData.tournamentSizeFormat || '5v5',
      first_journey_date: tournamentData.firstJourneyDate ? 
        new Date(`${tournamentData.firstJourneyDate}T${tournamentData.firstJourneyTime || '18:00'}`).toISOString() : null,
      created_by: userId,
      status: 'open',
      // Country/Region restrictions
      allowed_countries: tournamentData.allowed_countries || null,
      allowed_regions: tournamentData.allowed_regions || null,
      // Match configuration
      minutes_per_match: tournamentData.minutes_per_match || 12,
      golden_goal_overtime: tournamentData.golden_goal_overtime !== undefined ? tournamentData.golden_goal_overtime : true,
      mercy_rule: tournamentData.mercy_rule !== undefined ? tournamentData.mercy_rule : false,
      rules: tournamentData.rules || [],
      // Registration configuration
      invite_only: tournamentData.invite_only || false,
      // Swiss/Playoff configuration
      playoff_series_type: tournamentData.playoff_series_type || null,
      knockout_series_type: tournamentData.knockout_series_type || null,
      ercl_playoff_series_type: tournamentData.ercl_playoff_series_type || null,
      // Discord link
      discord_link: tournamentData.discord_link || null,
      // Tournament section
      section_id: tournamentData.sectionId || null,
      // Official streams
      official_streams: tournamentData.official_streams || []
    };

    const { data, error } = await supabase
    .from('tournaments')
    .insert(dbTournamentData)
    .select()
    .single();

    if (error) throw error;
    return { success: true, data };
  }, 'creating tournament');
};

export const checkTournamentReadiness = async (tournamentId) => {
  return dbOperation(async () => {
    const { data: tournament, error } = await supabase
    .from('tournaments')
    .select(`
        *,
        registrations:tournament_registrations(count)
      `)
    .eq('id', tournamentId)
    .single();

    if (error) throw error;

    const registeredTeams = tournament.registrations[0]?.count || 0;
    const minTeams = tournament.min_teams || 4;
    const maxTeams = tournament.max_teams;
    const canStart = registeredTeams >= minTeams && !tournament.bracket_generated;

    return {
      success: true,
      data: {
        canStart,
        isFull: registeredTeams >= maxTeams,
        registeredTeams,
        minTeams,
        maxTeams,
        bracketGenerated: tournament.bracket_generated,
        status: tournament.status,
        format: tournament.format
      }
    };
  }, 'checking tournament readiness');
};

// ========================================
// 🥅 TOURNAMENT BRACKET GENERATION
// ========================================

export const generateTournamentBracket = async (tournamentId, userId, config = {}) => {
  return dbOperation(async () => {
    await verifyTournamentManagement(userId, tournamentId);

    const readinessCheck = await checkTournamentReadiness(tournamentId);
    if (!readinessCheck.success || !readinessCheck.data.canStart) {
      throw new Error(readinessCheck.data?.error || 'Tournament cannot be started');
    }

    const tournament = readinessCheck.data;
    
    // Get full tournament data for accessing games_per_matchup
    const { data: tournamentData, error: tournamentError } = await supabase
      .from('tournaments')
      .select('*')
      .eq('id', tournamentId)
      .single();
    
    if (tournamentError) throw tournamentError;
    let result;

    switch (tournament.format) {
      case 'single_elimination':
        result = await supabase.rpc('generate_single_elimination_bracket', {
          tournament_id_param: tournamentId
        });
        break;
      case 'bo3':
      case 'bo5':
        // Use the new series-aware bracket generation
        result = await supabase.rpc('generate_single_elimination_bracket_with_series', {
          tournament_id_param: tournamentId
        });
        break;
      case 'league':
        result = await supabase.rpc('generate_league_fixtures_with_journeys', {
          tournament_id_param: tournamentId,
          games_per_matchup_param: tournamentData.games_per_matchup || 1
        });
        break;
      case 'group_stage_knockout':
        // Enhanced with dynamic group configuration (backward compatible)
        result = await supabase.rpc('generate_group_stage_knockout_tournament', {
          tournament_id_param: tournamentId,
          series_type_param: config?.knockout_series_type || 'single'
        });
        break;
      case 'versus':
        // Use the specialized versus function with configuration
        return await generateVersusTournament(tournamentId, config, userId);
      case 'swiss':
        result = await supabase.rpc('generate_swiss_bracket', {
          tournament_id_param: tournamentId
        });
        break;
      case 'ercl':
        result = await supabase.rpc('generate_ercl_league_phase', {
          tournament_id_param: tournamentId
        });
        break;
      default:
        throw new Error(`Tournament format ${tournament.format} not implemented`);
    }

    if (result.error) throw result.error;
    
    // Initialize tournament matches (auto-start first round)
    const initResult = await supabase.rpc('initialize_tournament_matches', {
      tournament_id_param: tournamentId
    });
    
    if (initResult.error) {
      console.warn('Failed to initialize tournament matches:', initResult.error);
      // Don't fail the whole operation, just log the warning
    }
    
    return { success: true, data: result.data };
  }, 'generating tournament bracket', { timeout: 15000 }); // Longer timeout for complex bracket generation
};

export const getTournamentBracket = async (tournamentId, options = {}) => {
  // Build cache arguments to ensure proper cache key differentiation
  const cacheArgs = [tournamentId];
  
  // Include phase-specific options that affect the result
  if (options.erclPhase) {
    cacheArgs.push(`phase:${options.erclPhase}`);
  }
  
  // Include other options that might affect the result
  if (options.includeStats !== undefined) {
    cacheArgs.push(`stats:${options.includeStats}`);
  }
  
  if (options.forceRefresh) {
    cacheArgs.push(`refresh:${Date.now()}`);
  }
  
  // Create enhanced options with proper cache key generation
  const enhancedOptions = {
    ...options,
    cacheArgs
  };
  
  return dbOperation(async () => {
    const { data: tournament, error: tournamentError } = await supabase
    .from('tournaments')
    .select('format, status, group_assignment_type, group_a_name, group_b_name, games_per_matchup, playoff_series_type, playoff_bracket_generated, knockout_series_type, knockout_bracket_generated')
    .eq('id', tournamentId)
    .single();

    if (tournamentError) throw tournamentError;

    const matchesResult = await getTournamentMatches(tournamentId);
    if (!matchesResult.success) throw new Error(matchesResult.error);

    const matches = matchesResult.data;

    if (tournament.format === 'league') {
      const standingsResult = await getLeagueStandings(tournamentId);
      if (!standingsResult.success) throw new Error(standingsResult.error);

      return {
        success: true,
        data: {
          type: 'league',
          standings: standingsResult.data,
          matches
        }
      };
    }

    if (tournament.format === 'versus') {
      const [standingsResult, groupsResult, winnersResult] = await Promise.all([
        getVersusStandings(tournamentId),
        getTournamentGroups(tournamentId),
        getVersusWinners(tournamentId)
      ]);

      if (!standingsResult.success) throw new Error(standingsResult.error);
      if (!groupsResult.success) throw new Error(groupsResult.error);

      return {
        success: true,
        data: {
          type: 'versus',
          standings: standingsResult.data,
          groups: groupsResult.data,
          matches,
          winners: winnersResult.success ? winnersResult.data : null,
          config: {
            assignmentType: tournament.group_assignment_type,
            groupAName: tournament.group_a_name,
            groupBName: tournament.group_b_name,
            gamesPerMatchup: tournament.games_per_matchup
          }
        }
      };
    }

    if (tournament.format === 'group_stage_knockout') {
      const groupsResult = await getTournamentGroups(tournamentId);
      if (!groupsResult.success) throw new Error(groupsResult.error);

      // Check if tournament is in knockout phase or completed with knockout bracket
      if ((tournament.status === 'knockout' || tournament.status === 'completed') && tournament.knockout_bracket_generated) {
        // Get qualified teams for seeding info
        const qualifiedResult = await getGroupQualifiedTeams(tournamentId);
        
        // Get knockout matches (rounds 101+ for group stage knockout) and format as standard bracket
        const knockoutMatches = matches.filter(m => m.round_number > 100);
        
        // Group knockout matches by round for bracket display (same format as regular tournaments)
        const bracket = {};
        knockoutMatches.forEach(match => {
          const round = `round_${match.round_number - 100}`; // Convert round 101 -> round_1, round 102 -> round_2, etc.
          if (!bracket[round]) {
            bracket[round] = [];
          }
          
          // Ensure match objects have timezone fields for frontend compatibility
          const enhancedMatch = {
            ...match,
            scheduled_datetime: match.scheduled_datetime,
            scheduler_timezone: match.scheduler_timezone
          };
          
          bracket[round].push(enhancedMatch);
        });

        // Sort matches within each round by match_number
        Object.keys(bracket).forEach(round => {
          bracket[round].sort((a, b) => a.match_number - b.match_number);
        });

        return {
          success: true,
          data: {
            type: 'bracket', // Use standard bracket type for consistent UI
            bracket,
            groupContext: {
              qualified_teams: qualifiedResult.success ? qualifiedResult.data : [],
              knockout_series_type: tournament.knockout_series_type || 'bo3',
              groups: groupsResult.data
            }
          }
        };
      } else {
        // Regular group stage view
        const standingsResult = await getGroupStageStandings(tournamentId);
        
        const groupStageMatches = {};
        const knockoutMatches = {};

        matches.forEach(match => {
          if (match.group_id) {
            if (!groupStageMatches[match.group_id]) {
              groupStageMatches[match.group_id] = [];
            }
            groupStageMatches[match.group_id].push(match);
          } else {
            const round = `round_${match.round_number}`;
            if (!knockoutMatches[round]) {
              knockoutMatches[round] = [];
            }
            knockoutMatches[round].push(match);
          }
        });

        return {
          success: true,
          data: {
            type: 'group_stage_knockout',
            groups: groupsResult.data,
            groupStageMatches,
            knockoutMatches,
            standings: standingsResult.success ? standingsResult.data : []
          }
        };
      }
    }

    if (tournament.format === 'swiss') {
      const standingsResult = await getSwissStandings(tournamentId);
      if (!standingsResult.success) throw new Error(standingsResult.error);

      // Check if tournament is in playoffs phase or completed
      if (['playoffs', 'completed'].includes(tournament.status) && tournament.playoff_bracket_generated) {
        // Get qualified teams for seeding info
        const qualifiedResult = await getSwissQualifiedTeams(tournamentId);
        
        // Get playoff matches (rounds 101+) and format as standard bracket
        const playoffMatches = matches.filter(m => m.round_number > 100);
        
        // Group playoff matches by round for bracket display (same format as regular tournaments)
        const bracket = {};
        playoffMatches.forEach(match => {
          const round = `round_${match.round_number - 100}`; // Convert 101 -> round_1, 102 -> round_2, etc.
          if (!bracket[round]) {
            bracket[round] = [];
          }
          
          // Ensure match objects have timezone fields for frontend compatibility
          const enhancedMatch = {
            ...match,
            scheduled_datetime: match.scheduled_datetime,
            scheduler_timezone: match.scheduler_timezone
          };
          
          bracket[round].push(enhancedMatch);
        });

        // Sort matches within each round by match_number
        Object.keys(bracket).forEach(round => {
          bracket[round].sort((a, b) => a.match_number - b.match_number);
        });

        return {
          success: true,
          data: {
            type: 'bracket', // Use standard bracket type for consistent UI
            bracket,
            swissContext: {
              qualified_teams: qualifiedResult.success ? qualifiedResult.data : [],
              playoff_series_type: tournament.playoff_series_type || 'bo3',
              swiss_standings: standingsResult.data
            }
          }
        };
      } else {
        // Regular Swiss tournament view
        return {
          success: true,
          data: {
            type: 'swiss',
            standings: standingsResult.data,
            matches
          }
        };
      }
    }

    if (tournament.format === 'ercl') {
      const standingsResult = await getERCLStandings(tournamentId);
      if (!standingsResult.success) throw new Error(standingsResult.error);

      // For ERCL, we need to work with the actual matches, not series
      // Extract matches from series format
      const flatMatches = [];
      matches.forEach(series => {
        if (series.games && Array.isArray(series.games)) {
          flatMatches.push(...series.games);
        } else {
          // Fallback in case it's already a flat match
          flatMatches.push(series);
        }
      });
      
      // Check tournament status to determine what to display
      if (tournament.status === 'knockout' || (tournament.status === 'completed' && flatMatches.some(m => m.round_number >= 201))) {
        // Handle phase-specific requests
        const { erclPhase } = options;
        const knockoutMatches = flatMatches.filter(m => m.round_number >= 201);
        const playoffMatches = flatMatches.filter(m => m.round_number === 101);
        
        const bracket = {};
        
        if (erclPhase === 'playoffs') {
          // Playoffs tab: show only playoff matches
          // Convert flat matches back to series format for frontend compatibility
          if (playoffMatches.length > 0) {
            
            const playoffSeries = playoffMatches.reduce((acc, match) => {
              if (!acc[match.series_id]) {
                acc[match.series_id] = {
                  series_id: match.series_id,
                  series_type: match.series_type,
                  games_to_win: match.games_to_win,
                  round_number: match.round_number,
                  match_number: match.match_number,
                  team1: match.team1,
                  team2: match.team2,
                  series_winner_id: match.series_winner_id,
                  series_complete: match.series_complete,
                  status: match.series_complete ? 'completed' : 'pending',
                  winner_id: match.series_winner_id,
                  team1_score: match.team1_score,
                  team2_score: match.team2_score,
                  team1_vod_url: match.team1_vod_url,
                  team2_vod_url: match.team2_vod_url,
                  match_date: match.match_date,
                  match_time: match.match_time,
                  scheduled_datetime: match.scheduled_datetime,
                  scheduler_timezone: match.scheduler_timezone,
                  player_stats: match.player_stats || [],
                  group_id: match.group_id,
                  games: []
                };
              } else {
                // Update series timezone data if this match has scheduling info and series doesn't
                if (match.scheduled_datetime && !acc[match.series_id].scheduled_datetime) {
                  acc[match.series_id].scheduled_datetime = match.scheduled_datetime;
                  acc[match.series_id].scheduler_timezone = match.scheduler_timezone;
                  acc[match.series_id].match_date = match.match_date;
                  acc[match.series_id].match_time = match.match_time;
                }
              }
              acc[match.series_id].games.push(match);
              return acc;
            }, {});
            
            const sortedPlayoffSeries = Object.values(playoffSeries).sort((a, b) => a.match_number - b.match_number);
            bracket['round_0'] = sortedPlayoffSeries;
            
          }
        } else if (erclPhase === 'knockout') {
          // Knockout tab: show only knockout matches
          
          // Convert flat matches back to series format for frontend compatibility
          const knockoutSeries = knockoutMatches.reduce((acc, match) => {
            if (!acc[match.series_id]) {
              acc[match.series_id] = {
                series_id: match.series_id,
                series_type: match.series_type,
                games_to_win: match.games_to_win,
                round_number: match.round_number,
                match_number: match.match_number,
                team1: match.team1,
                team2: match.team2,
                team1_id: match.team1_id, // Add team IDs for getTeamDisplayInfo
                team2_id: match.team2_id, // Add team IDs for getTeamDisplayInfo
                series_winner_id: match.series_winner_id,
                series_complete: match.series_complete,
                status: match.series_complete ? 'completed' : 'pending',
                winner_id: match.series_winner_id,
                team1_score: match.team1_score,
                team2_score: match.team2_score,
                team1_vod_url: match.team1_vod_url,
                team2_vod_url: match.team2_vod_url,
                match_date: match.match_date,
                match_time: match.match_time,
                scheduled_datetime: match.scheduled_datetime,
                scheduler_timezone: match.scheduler_timezone,
                player_stats: match.player_stats || [],
                group_id: match.group_id,
                games: []
              };
            }
            acc[match.series_id].games.push(match);
            return acc;
          }, {});
          
          Object.values(knockoutSeries).forEach(series => {
            const round = `round_${series.round_number - 200}`; // Convert 201 -> round_1, etc.
            if (!bracket[round]) {
              bracket[round] = [];
            }
            bracket[round].push(series);
          });

          Object.keys(bracket).forEach(round => {
            bracket[round].sort((a, b) => a.match_number - b.match_number);
          });
          
        } else {
          // Default: show both (for backwards compatibility)
          // Convert playoff matches to series format and add as historical data (round_0)
          if (playoffMatches.length > 0) {
            const playoffSeries = playoffMatches.reduce((acc, match) => {
              if (!acc[match.series_id]) {
                acc[match.series_id] = {
                  series_id: match.series_id,
                  series_type: match.series_type,
                  games_to_win: match.games_to_win,
                  round_number: match.round_number,
                  match_number: match.match_number,
                  team1: match.team1,
                  team2: match.team2,
                  series_winner_id: match.series_winner_id,
                  series_complete: match.series_complete,
                  status: match.series_complete ? 'completed' : 'pending',
                  winner_id: match.series_winner_id,
                  team1_score: match.team1_score,
                  team2_score: match.team2_score,
                  team1_vod_url: match.team1_vod_url,
                  team2_vod_url: match.team2_vod_url,
                  match_date: match.match_date,
                  match_time: match.match_time,
                  scheduled_datetime: match.scheduled_datetime,
                  scheduler_timezone: match.scheduler_timezone,
                  player_stats: match.player_stats || [],
                  group_id: match.group_id,
                  games: []
                };
              }
              acc[match.series_id].games.push(match);
              return acc;
            }, {});
            bracket['round_0'] = Object.values(playoffSeries).sort((a, b) => a.match_number - b.match_number);
          }
          
          // Convert knockout matches to series format
          const knockoutSeries = knockoutMatches.reduce((acc, match) => {
            if (!acc[match.series_id]) {
              acc[match.series_id] = {
                series_id: match.series_id,
                series_type: match.series_type,
                games_to_win: match.games_to_win,
                round_number: match.round_number,
                match_number: match.match_number,
                team1: match.team1,
                team2: match.team2,
                series_winner_id: match.series_winner_id,
                series_complete: match.series_complete,
                status: match.series_complete ? 'completed' : 'pending',
                winner_id: match.series_winner_id,
                team1_score: match.team1_score,
                team2_score: match.team2_score,
                team1_vod_url: match.team1_vod_url,
                team2_vod_url: match.team2_vod_url,
                match_date: match.match_date,
                match_time: match.match_time,
                scheduled_datetime: match.scheduled_datetime,
                scheduler_timezone: match.scheduler_timezone,
                player_stats: match.player_stats || [],
                group_id: match.group_id,
                games: []
              };
            }
            acc[match.series_id].games.push(match);
            return acc;
          }, {});
          
          // Add knockout matches
          Object.values(knockoutSeries).forEach(series => {
            const round = `round_${series.round_number - 200}`; // Convert 201 -> round_1, etc.
            if (!bracket[round]) {
              bracket[round] = [];
            }
            bracket[round].push(series);
          });

          Object.keys(bracket).forEach(round => {
            if (round !== 'round_0') { // Don't sort playoff round again
              bracket[round].sort((a, b) => a.match_number - b.match_number);
            }
          });
        }

        return {
          success: true,
          data: {
            type: 'bracket',
            bracket,
            erclContext: {
              phase: 'knockout',
              league_standings: standingsResult.data,
              show_playoff_history: true // Flag to indicate playoff history is included
            }
          }
        };
      } else if (tournament.status === 'playoffs' || flatMatches.some(m => m.round_number === 101)) {
        // Playoff phase - show playoff bracket
        const playoffMatches = flatMatches.filter(m => m.round_number === 101);
        
        // Convert to series format for frontend compatibility
        const playoffSeries = playoffMatches.reduce((acc, match) => {
          if (!acc[match.series_id]) {
            acc[match.series_id] = {
              series_id: match.series_id,
              series_type: match.series_type,
              games_to_win: match.games_to_win,
              round_number: match.round_number,
              match_number: match.match_number,
              team1: match.team1,
              team2: match.team2,
              series_winner_id: match.series_winner_id,
              series_complete: match.series_complete,
              status: match.series_complete ? 'completed' : 'pending',
              winner_id: match.series_winner_id,
              team1_score: match.team1_score,
              team2_score: match.team2_score,
              team1_vod_url: match.team1_vod_url,
              team2_vod_url: match.team2_vod_url,
              match_date: match.match_date,
              match_time: match.match_time,
              scheduled_datetime: match.scheduled_datetime,
              scheduler_timezone: match.scheduler_timezone,
              player_stats: match.player_stats || [],
              group_id: match.group_id,
              games: []
            };
          } else {
            // Update series timezone data if this match has scheduling info and series doesn't
            if (match.scheduled_datetime && !acc[match.series_id].scheduled_datetime) {
              acc[match.series_id].scheduled_datetime = match.scheduled_datetime;
              acc[match.series_id].scheduler_timezone = match.scheduler_timezone;
              acc[match.series_id].match_date = match.match_date;
              acc[match.series_id].match_time = match.match_time;
            }
          }
          acc[match.series_id].games.push(match);
          return acc;
        }, {});
        
        const bracket = {};
        Object.values(playoffSeries).forEach(series => {
          const round = 'round_1'; // All playoff matches are round of 16
          if (!bracket[round]) {
            bracket[round] = [];
          }
          bracket[round].push(series);
        });

        Object.keys(bracket).forEach(round => {
          bracket[round].sort((a, b) => a.match_number - b.match_number);
        });

        return {
          success: true,
          data: {
            type: 'bracket',
            bracket,
            erclContext: {
              phase: 'playoffs',
              league_standings: standingsResult.data
            }
          }
        };
      } else {
        // League phase - show standings and matches
        return {
          success: true,
          data: {
            type: 'ercl',
            standings: standingsResult.data,
            matches: flatMatches,
            erclContext: {
              phase: 'league'
            }
          }
        };
      }
    }

    // Single elimination and other bracket formats
    const bracket = {};
    matches.forEach(match => {
      const round = `round_${match.round_number}`;
      if (!bracket[round]) {
        bracket[round] = [];
      }
      bracket[round].push(match);
    });

    Object.keys(bracket).forEach(round => {
      bracket[round].sort((a, b) => a.match_number - b.match_number);
    });

    return {
      success: true,
      data: {
        type: 'bracket',
        bracket
      }
    };
  }, 'fetching tournament bracket', enhancedOptions);
};

export const getLeagueStandings = async (tournamentId, groupId = null) => {
  return dbOperation(async () => {
    const { data, error } = await supabase.rpc('get_league_standings', {
      tournament_id_param: tournamentId,
      group_id_param: groupId
    });

    if (error) throw error;
    return { success: true, data };
  }, 'fetching league standings');
};

export const getSwissStandings = async (tournamentId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase.rpc('get_swiss_standings', {
      tournament_id_param: tournamentId
    });

    if (error) throw error;
    return { success: true, data };
  }, 'fetching swiss standings', { timeout: 12000 }); // Longer timeout for complex standings calculation
};

export const getSwissQualifiedTeams = async (tournamentId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase.rpc('get_swiss_qualified_teams', {
      tournament_id_param: tournamentId
    });

    if (error) throw error;
    return { success: true, data };
  }, 'fetching Swiss qualified teams');
};

export const advanceSwissTournament = async (tournamentId, userId) => {
  return dbOperation(async () => {
    // Verify tournament management permissions
    await verifyTournamentManagement(userId, tournamentId);
    
    const { data, error } = await supabase.rpc('advance_swiss_tournament', {
      tournament_id_param: tournamentId
    });

    if (error) throw error;
    return { success: true, data };
  }, 'advancing Swiss tournament');
};

// ========================================
// 🏆 ERCL TOURNAMENT FUNCTIONS
// ========================================

export const getERCLStandings = async (tournamentId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase.rpc('get_ercl_league_standings', {
      tournament_id_param: tournamentId
    });

    if (error) throw error;
    return { success: true, data };
  }, 'fetching ERCL league standings');
};

export const getTournamentGroups = async (tournamentId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .from('tournament_groups')
    .select(`
        *,
        team_assignments:team_group_assignments(
          team:teams(id, name, logo)
        )
      `)
    .eq('tournament_id', tournamentId)
    .order('group_number');

    if (error) throw error;
    return { success: true, data };
  }, 'fetching tournament groups');
};

// ========================================
// 🏟️ GROUP STAGE + KNOCKOUT FUNCTIONS
// ========================================

export const getGroupStageStandings = async (tournamentId, groupId = null) => {
  return dbOperation(async () => {
    const { data, error } = await supabase.rpc('get_group_stage_standings', {
      tournament_id_param: tournamentId,
      group_id_param: groupId
    });

    if (error) throw error;
    return { success: true, data };
  }, 'fetching group stage standings', { 
    timeout: 12000,
    cacheArgs: [tournamentId, groupId] // Include parameters in cache key
  });
};

export const getGroupQualifiedTeams = async (tournamentId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase.rpc('get_group_qualified_teams', {
      tournament_id_param: tournamentId
    });

    if (error) throw error;
    return { success: true, data };
  }, 'fetching group qualified teams');
};

// ========================================
// 🆚 VERSUS TOURNAMENT FUNCTIONS
// ========================================

/**
 * Generate versus tournament with custom parameters
 */
export const generateVersusTournament = async (tournamentId, config, userId) => {
  return dbOperation(async () => {
    await verifyTournamentManagement(userId, tournamentId);

    const {
      assignmentType = 'random',
      groupAName = 'Group A',
      groupBName = 'Group B',
      gamesPerMatchup = 1,
      groupATeams = null,
      groupBTeams = null
    } = config;

    const result = await supabase.rpc('generate_versus_fixtures_with_journeys', {
      tournament_id_param: tournamentId,
      assignment_type: assignmentType,
      group_a_name_param: groupAName,
      group_b_name_param: groupBName,
      games_per_matchup_param: gamesPerMatchup,
      group_a_teams: groupATeams,
      group_b_teams: groupBTeams
    });

    if (result.error) throw result.error;
    return { success: true, data: result.data };
  }, 'generating versus tournament');
};

/**
 * Get versus tournament standings (individual + group)
 */
export const getVersusStandings = async (tournamentId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase.rpc('get_versus_standings', {
      tournament_id_param: tournamentId
    });

    if (error) throw error;
    return { success: true, data };
  }, 'fetching versus standings');
};

/**
 * Get versus tournament winners (both group and individual)
 */
export const getVersusWinners = async (tournamentId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase.rpc('get_versus_group_winners', {
      tournament_id_param: tournamentId
    });

    if (error) throw error;
    return { success: true, data: data[0] || null };
  }, 'fetching versus winners');
};

// ========================================
// 👥 TEAM FUNCTIONS
// ========================================

export const getUserTeams = async (userId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .from('team_members')
    .select(`
        team:teams(${teamWithMembersQuery()})
      `)
    .eq('user_id', userId);

    if (error) throw error;

    const teamsWithStats = await Promise.all(
        data.map(async (item) => {
          const team = item.team;
          const { data: teamStats } = await supabase
          .rpc('get_team_leaderboards')
          .eq('team_id', team.id)
          .single();

          return {
            ...team,
            dynamicStats: teamStats || {
              tournaments_won: 0,
              match_wins: 0,
              match_losses: 0,
              total_team_goals: 0,
              total_matches: 0
            }
          };
        })
    );

    return { success: true, data: teamsWithStats };
  }, 'fetching user teams');
};

export const createTeam = async (teamData, userId) => {
  return dbOperation(async () => {
    const { data: team, error: teamError } = await supabase
    .from('teams')
    .insert({
      name: teamData.name,
      description: teamData.description,
      country_code: teamData.country_code,
      logo: teamData.logo,
      owner_id: userId,
      is_active: true
    })
    .select()
    .single();

    if (teamError) throw teamError;

    const { error: memberError } = await supabase
    .from('team_members')
    .insert({
      team_id: team.id,
      user_id: userId,
      role: 'owner'
    });

    if (memberError) throw memberError;
    return { success: true, data: team };
  }, 'creating team');
};

export const updateTeam = async (teamId, updates, userId) => {
  return dbOperation(async () => {
    await verifyTeamOwner(teamId, userId);

    const { data, error } = await supabase
    .from('teams')
    .update(updates)
    .eq('id', teamId)
    .select()
    .single();

    if (error) throw error;
    return { success: true, data };
  }, 'updating team');
};

export const deleteTeam = async (teamId, userId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('soft_delete_team', {
        team_id_param: teamId
      });
    
    if (error) throw error;
    
    if (data && data.success === false) {
      throw new Error(data.error || 'Failed to delete team');
    }
    
    return { success: true, message: data?.message || 'Team successfully deleted' };
  }, 'deleting team');
};

export const inviteToTeam = async (teamId, username, invitedBy) => {
  return dbOperation(async () => {
    const user = await findUser(username);

    const { data, error } = await supabase
    .from('team_invitations')
    .insert({
      team_id: teamId,
      invited_user_id: user.id,
      invited_by: invitedBy
    })
    .select(`
        *,
        team:teams(name),
        invited_by_user:users(username)
      `)
    .single();

    if (error) throw error;
    return { success: true, data };
  }, 'sending team invitation');
};

export const respondToInvitation = async (invitationId, response, userId) => {
  return dbOperation(async () => {
    const { data: invitation, error: updateError } = await supabase
    .from('team_invitations')
    .update({
      status: response,
      responded_at: new Date().toISOString()
    })
    .eq('id', invitationId)
    .eq('invited_user_id', userId)
    .select()
    .single();

    if (updateError) throw updateError;

    if (response === 'accepted') {
      const { error: memberError } = await supabase
      .from('team_members')
      .insert({
        team_id: invitation.team_id,
        user_id: userId,
        role: 'player'
      });

      if (memberError) throw memberError;
    }

    return { success: true, data: invitation };
  }, 'responding to invitation');
};

// ========================================
// 👑 TEAM ROLE MANAGEMENT
// ========================================

const verifyTeamOwnerOnly = async (teamId, userId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .from('team_members')
      .select('role')
      .eq('team_id', teamId)
      .eq('user_id', userId)
      .single();
    
    if (error) throw error;
    if (!data || data.role !== 'owner') {
      throw new Error('Only team owners can perform this action');
    }
    return data;
  }, 'verifying team owner permissions');
};

export const promoteToTeamCaptain = async (teamId, userId, requestingUserId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('promote_to_team_captain', {
        team_id_param: teamId,
        user_id_param: userId,
        requesting_user_id: requestingUserId
      });

    if (error) throw error;
    
    if (data && data.success === false) {
      throw new Error(data.error || 'Failed to promote member to captain');
    }
    
    return { success: true, data };
  }, 'promoting team member to captain');
};

export const demoteFromTeamCaptain = async (teamId, userId, requestingUserId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('demote_from_team_captain', {
        team_id_param: teamId,
        user_id_param: userId,
        requesting_user_id: requestingUserId
      });

    if (error) throw error;
    
    if (data && data.success === false) {
      throw new Error(data.error || 'Failed to demote captain to player');
    }
    
    return { success: true, data };
  }, 'demoting team captain to player');
};

export const sendTeamInvitation = async (teamId, invitedUserId, message, requestingUserId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('send_team_invitation', {
        team_id_param: teamId,
        invited_user_id_param: invitedUserId,
        message_param: message,
        requesting_user_id: requestingUserId
      });

    if (error) throw error;
    
    if (data && data.success === false) {
      throw new Error(data.error || 'Failed to send team invitation');
    }
    
    return { success: true, data };
  }, 'sending team invitation');
};

export const updateTeamInfo = async (teamId, updates, requestingUserId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('update_team_info', {
        team_id_param: teamId,
        team_name: updates.name || null,
        team_description: updates.description || null,
        team_logo: updates.logo || null,
        team_country_code: updates.country_code || null,
        team_recruitment_status: updates.recruitment_status || null,
        requesting_user_id: requestingUserId
      });

    if (error) throw error;
    
    if (data && data.success === false) {
      throw new Error(data.error || 'Failed to update team information');
    }
    
    return { success: true, data: data.team };
  }, 'updating team information');
};

export const getTeamInvitations = async (teamId, requestingUserId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('get_team_invitations', {
        team_id_param: teamId,
        requesting_user_id: requestingUserId
      });

    if (error) throw error;
    
    return { success: true, data: data || [] };
  }, 'fetching team invitations');
};

export const transferTeamOwnership = async (teamId, newOwnerId, requestingUserId) => {
  return dbOperation(async () => {
    await verifyTeamOwnerOnly(teamId, requestingUserId);
    
    const { data, error } = await supabase
      .rpc('transfer_team_ownership', {
        team_id_param: teamId,
        new_owner_id: newOwnerId
      });

    if (error) throw error;
    
    if (data && data.length > 0) {
      const result = data[0];
      if (!result.success) {
        throw new Error(result.error || 'Failed to transfer team ownership');
      }
    }
    
    return { success: true, data };
  }, 'transferring team ownership');
};

// ========================================
// 📝 TOURNAMENT REGISTRATION
// ========================================

const checkTournamentConflicts = async (tournamentId, teamId) => {
  const { data: teamMembers, error: teamError } = await supabase
  .from('team_members')
  .select('user_id, user:users(username, display_name)')
  .eq('team_id', teamId);

  if (teamError) throw teamError;
  if (!teamMembers?.length) {
    throw new Error('Team has no members');
  }

  const { data: registeredTeams, error: regError } = await supabase
  .from('tournament_registrations')
  .select(`
      team_id,
      team:teams(
        id, name,
        members:team_members(
          user_id,
          user:users(username, display_name)
        )
      )
    `)
  .eq('tournament_id', tournamentId)
  .neq('team_id', teamId);

  if (regError) throw regError;

  const conflicts = [];
  const teamMemberIds = teamMembers.map(member => member.user_id);

  for (const registration of registeredTeams || []) {
    for (const registeredMember of registration.team.members || []) {
      if (teamMemberIds.includes(registeredMember.user_id)) {
        const conflictingUser = teamMembers.find(m => m.user_id === registeredMember.user_id);
        conflicts.push({
          userId: registeredMember.user_id,
          username: conflictingUser?.user?.username || conflictingUser?.user?.display_name || 'Unknown User',
          conflictingTeam: {
            id: registration.team.id,
            name: registration.team.name
          }
        });
      }
    }
  }

  if (conflicts.length > 0) {
    return {
      success: false,
      conflicts,
      error: `${conflicts.length} team member(s) are already registered for this tournament with other teams`
    };
  }

  return { success: true };
};

/**
 * Check team eligibility for tournament registration
 */
export const checkTeamTournamentEligibility = async (tournamentId, teamId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('check_team_tournament_eligibility', {
        tournament_id_param: tournamentId,
        team_id_param: teamId
      });

    if (error) throw error;
    
    // Debug logging removed
    
    return { success: true, data };
  }, 'checking team tournament eligibility');
};

export const registerTeamForTournament = async (tournamentId, teamId) => {
  return dbOperation(async () => {
    // First check eligibility using the database function
    const eligibilityCheck = await checkTeamTournamentEligibility(tournamentId, teamId);
    if (!eligibilityCheck.success) {
      return eligibilityCheck;
    }

    // Handle eligibility check - treat null as eligible if no errors exist (temporary fix for DB bug)
    const isEligible = eligibilityCheck.data.eligible === true || 
                      (eligibilityCheck.data.eligible === null && 
                       (!eligibilityCheck.data.errors || eligibilityCheck.data.errors.length === 0));
                       
    if (!isEligible) {
      throw new Error(eligibilityCheck.data.errors[0] || 'Team is not eligible for this tournament');
    }

    // Check for conflicts (keeping existing logic for additional validation)
    const conflictCheck = await checkTournamentConflicts(tournamentId, teamId);
    if (!conflictCheck.success) {
      return conflictCheck;
    }

    // Register team
    const { data, error } = await supabase
    .from('tournament_registrations')
    .insert({
      tournament_id: tournamentId,
      team_id: teamId,
      registered_at: new Date().toISOString()
    })
    .select()
    .single();

    if (error) throw error;
    return { success: true, data };
  }, 'registering team for tournament');
};

/**
 * Register team for tournament as admin (bypasses status check)
 */
export const adminRegisterTeamForTournament = async (tournamentId, teamId, adminId) => {
  return dbOperation(async () => {
    // Verify admin permissions first
    await verifyTournamentManagement(adminId, tournamentId);

    // Use RPC function to bypass RLS policies
    const { data: result, error: rpcError } = await supabase
      .rpc('admin_register_team_for_tournament', {
        tournament_id_param: tournamentId,
        team_id_param: teamId,
        admin_id_param: adminId
      });

    if (rpcError) {
      throw new Error(`Failed to register team: ${rpcError.message}`);
    }

    if (result && result.success) {
      console.log(`Registered team ${result.team_name} for tournament ${result.tournament_name}`);
      return result;
    } else if (result && !result.success) {
      throw new Error(result.error);
    } else {
      throw new Error('Unexpected error during team registration');
    }
  }, 'admin registering team for tournament');
};

export const checkUserTournamentRegistration = async (tournamentId, userId) => {
  return dbOperation(async () => {
    const { data: registrations, error } = await supabase
    .from('tournament_registrations')
    .select(`
        team:teams(
          id, name,
          members:team_members(user_id)
        )
      `)
    .eq('tournament_id', tournamentId);

    if (error) throw error;

    for (const registration of registrations || []) {
      const isUserInTeam = registration.team.members?.some(member => member.user_id === userId);
      if (isUserInTeam) {
        return {
          isRegistered: true,
          teamName: registration.team.name,
          teamId: registration.team.id
        };
      }
    }

    return { isRegistered: false };
  }, 'checking user tournament registration', {
    cacheArgs: [tournamentId, userId]  // Include tournamentId and userId in cache key
  });
};

/**
 * Remove a team from a tournament (admin only)
 * Uses RPC function to bypass RLS policies
 */
/**
 * Update tournament details (admins, creators, and owners can edit, open tournaments only)
 */
export const updateTournament = async (tournamentId, updateData, userId) => {
  return dbOperation(async () => {
    await verifyTournamentManagement(userId, tournamentId);

    // Prepare the update data with only allowed fields
    const allowedFields = [
      'name', 'description', 'prize_pool', 'start_date', 'start_time', 
      'max_teams', 'min_teams', 'platform', 'allowed_countries', 'allowed_regions', 'logo',
      'discord_link', 'playoff_series_type', 'knockout_series_type', 'invite_only',
      'minutes_per_match', 'golden_goal_overtime', 'mercy_rule', 'rules', 'official_streams'
    ];

    const filteredData = {};
    allowedFields.forEach(field => {
      if (updateData[field] !== undefined) {
        filteredData[field] = updateData[field];
      }
    });

    // Add updated_at timestamp
    filteredData.updated_at = new Date().toISOString();

    // Update the tournament
    const { data: updatedTournament, error: updateError } = await supabase
      .from('tournaments')
      .update(filteredData)
      .eq('id', tournamentId)
      .select();

    if (updateError) throw updateError;
    
    if (!updatedTournament || updatedTournament.length === 0) {
      throw new Error('Failed to update tournament - no rows affected');
    }

    console.log(`Updated tournament ${updatedTournament[0].name}`);

    return {
      success: true,
      data: updatedTournament[0]
    };
  }, 'updating tournament');
};

export const removeTeamFromTournament = async (tournamentId, teamId, userId) => {
  return dbOperation(async () => {
    await verifyTournamentManagement(userId, tournamentId);

    // Use RPC function to bypass RLS policies
    const { data: result, error: rpcError } = await supabase
      .rpc('admin_remove_team_from_tournament', {
        tournament_id_param: tournamentId,
        team_id_param: teamId,
        admin_id_param: userId
      });

    if (rpcError) {
      throw new Error(`Failed to remove team: ${rpcError.message}`);
    }

    if (result && result.success) {
      console.log(`Removed team ${result.team_name} from tournament ${result.tournament_name}`);
      return result;
    } else if (result && !result.success) {
      throw new Error(result.error);
    } else {
      throw new Error('Unexpected error during team removal');
    }
  }, 'removing team from tournament');
};

// ========================================
// ⚽ MATCH FUNCTIONS
// ========================================

export const getTournamentMatches = async (tournamentId) => {
  return dbOperation(async () => {
    const { data: matches, error } = await supabase
      .from('matches')
      .select(`
        *, 
        scheduled_datetime,
        scheduler_timezone,
        team1:team1_id(id, name, logo),
        team2:team2_id(id, name, logo),
        winner:winner_id(id, name, logo),
        bye_team:bye_team_id(id, name, logo),
        player_stats:player_match_stats(
          id,
          player_id,
          team_id,
          goals,
          assists,
          passes,
          interceptions,
          saves,
          player:player_id(id, username, display_name, avatar_url, platform)
        ),
        schedule_requests:match_schedule_requests!match_id(
          id,
          proposer_team_id,
          opponent_team_id,
          proposed_date,
          proposed_time,
          timezone,
          status,
          created_at,
          expires_at,
          proposer_team:proposer_team_id(id, name),
          opponent_team:opponent_team_id(id, name)
        )
      `)
      .eq('tournament_id', tournamentId)
      .order('round_number, match_number, game_number');

    if (error) throw error;

    // Debug: Log matches with timezone data
    const matchesWithTimezone = matches.filter(m => m.scheduled_datetime);
    if (matchesWithTimezone.length > 0) {
      console.log('Database: Found matches with timezone data', matchesWithTimezone.map(m => ({
        id: m.id,
        scheduled_datetime: m.scheduled_datetime,
        scheduler_timezone: m.scheduler_timezone,
        match_date: m.match_date,
        match_time: m.match_time
      })));
    }

    // Group by series_id
    const seriesMap = matches.reduce((acc, match) => {
      if (!acc[match.series_id]) {
        acc[match.series_id] = {
          series_id: match.series_id,
          series_type: match.series_type,
          games_to_win: match.games_to_win,
          round_number: match.round_number,
          match_number: match.match_number,
          team1: match.team1,
          team2: match.team2,
          series_winner_id: match.series_winner_id,
          series_complete: match.series_complete,
          status: match.series_complete ? 'completed' : 'pending', // Add series-level status
          winner_id: match.series_winner_id, // Add series-level winner_id for consistency
          // Add scores for single matches (versus tournaments)
          team1_score: match.series_type === 'single' ? match.team1_score : null,
          team2_score: match.series_type === 'single' ? match.team2_score : null,
          // Add VOD URLs
          team1_vod_url: match.team1_vod_url,
          team2_vod_url: match.team2_vod_url,
          // Add match date/time and timezone info
          match_date: match.match_date,
          match_time: match.match_time,
          scheduled_datetime: match.scheduled_datetime,
          scheduler_timezone: match.scheduler_timezone,
          // Add player stats for single matches
          player_stats: match.series_type === 'single' ? match.player_stats : [],
          // Preserve group_id for group stage tournaments
          group_id: match.group_id,
          // Add schedule request data
          schedule_requests: match.schedule_requests || [],
          active_schedule_request: (match.schedule_requests || []).find(req =>
            req.status === 'pending' || req.status === 'counter_proposed'
          ) || null,
          games: []
        };
      }
      acc[match.series_id].games.push(match);
      
      // Update series status and scores based on games
      const series = acc[match.series_id];
      if (match.series_complete) {
        series.status = 'completed';
        series.winner_id = match.series_winner_id;
        // For single matches, copy the scores and player stats to series level
        if (match.series_type === 'single') {
          series.team1_score = match.team1_score;
          series.team2_score = match.team2_score;
          series.player_stats = match.player_stats || [];
        }
      } else if (match.status === 'live') {
        series.status = 'live';
      } else if (match.status === 'completed') {
        series.status = 'in_progress';
      } else if (match.status === 'scheduled') {
        series.status = 'scheduled';
      }
      
      return acc;
    }, {});

    return { success: true, data: Object.values(seriesMap) };
  }, 'fetching tournament matches');
};

export const getMatchDetails = async (matchId) => {
  return dbOperation(async () => {
    // First try to get by ID from tournament matches table
    let { data, error } = await supabase
    .from('matches')
    .select(`
        ${matchWithDetailsQuery()},
        tournament:tournaments(id, name, format)
      `)
    .eq('id', matchId)
    .single();

    // If not found by ID, try by series_id and get the first game of the series
    if (error && error.code === 'PGRST116') {
      const { data: seriesData, error: seriesError } = await supabase
      .from('matches')
      .select(`
          ${matchWithDetailsQuery()},
          tournament:tournaments(id, name, format)
        `)
      .eq('series_id', matchId)
      .eq('game_number', 1)
      .single();
      
      if (seriesError && seriesError.code === 'PGRST116') {
        // If still not found, try to get from scrim_matches table
        const { data: scrimData, error: scrimError } = await supabase
        .from('scrim_matches')
        .select(`
            *,
            team1:teams!scrim_matches_team1_id_fkey(id, name, logo),
            team2:teams!scrim_matches_team2_id_fkey(id, name, logo),
            queue:scrim_queues(name, display_name, game_mode),
            players:scrim_match_players(
              *,
              player:users(id, username, display_name, avatar_url, gamer_tag)
            )
          `)
        .eq('id', matchId)
        .single();
        
        if (scrimError) throw scrimError;
        
        // Transform scrim match data to match tournament match format
        const transformedPlayerStats = scrimData.players?.map(player => ({
          id: `scrim_${player.player_id}_${scrimData.id}`, // Create unique ID
          match_id: scrimData.id,
          team_id: player.team_number === 1 ? scrimData.team1_id : scrimData.team2_id,
          player_id: player.player_id,
          goals: player.goals || 0,
          assists: player.assists || 0,
          passes: player.passes || 0,
          interceptions: player.interceptions || 0,
          saves: player.saves || 0,
          player: player.player // Include player info
        })) || [];

        data = {
          ...scrimData,
          match_type: 'scrim',
          tournament: null, // Scrim matches don't have tournaments
          team1_score: scrimData.team1_score,
          team2_score: scrimData.team2_score,
          winner_id: scrimData.winner_team_id,
          player_stats: transformedPlayerStats, // Add transformed player stats
          team1_id: scrimData.team1_id,
          team2_id: scrimData.team2_id
        };
      } else if (seriesError) {
        throw seriesError;
      } else {
        data = seriesData;
      }
    } else if (error) {
      throw error;
    }

    return { success: true, data };
  }, 'fetching match details');
};

export const updateMatchSchedule = async (matchId, scheduleData, userId) => {
  return dbOperation(async () => {
    // Get tournament ID and series ID from match to verify permissions and handle series scheduling
    const { data: matchData, error: matchError } = await supabase
      .from('matches')
      .select('tournament_id, series_id, series_type')
      .eq('id', matchId)
      .single();
    
    if (matchError) throw matchError;
    
    await verifyTournamentManagement(userId, matchData.tournament_id);

    // If new timezone-aware fields are provided, use the RPC function
    if (scheduleData.scheduled_datetime) {
      // For Bo3/Bo5 series, update all matches in the series with the same time
      if (matchData.series_id && (matchData.series_type === 'bo3' || matchData.series_type === 'bo5')) {
        
        // Update all matches in the series
        const { data: seriesMatches, error: seriesError } = await supabase
          .from('matches')
          .select('id')
          .eq('series_id', matchData.series_id);
        
        if (seriesError) throw seriesError;
        
        // Update each match in the series
        const updatePromises = seriesMatches.map(match => 
          supabase.rpc('schedule_match_with_timezone', {
            p_match_id: match.id,
            p_scheduled_datetime: scheduleData.scheduled_datetime,
            p_scheduler_timezone: scheduleData.scheduler_timezone || null
          })
        );
        
        const results = await Promise.all(updatePromises);
        const errors = results.filter(result => result.error);
        
        if (errors.length > 0) {
          throw new Error(`Failed to schedule ${errors.length} matches in series`);
        }
        
        return { success: true, data: results.map(r => r.data) };
      } else {
        // Single match scheduling
        const { data, error } = await supabase.rpc('schedule_match_with_timezone', {
          p_match_id: matchId,
          p_scheduled_datetime: scheduleData.scheduled_datetime,
          p_scheduler_timezone: scheduleData.scheduler_timezone || null
        });

        if (error) throw error;
        return { success: true, data };
      }
    }
    
    // Fallback to legacy update for backward compatibility
    const { data, error } = await supabase
    .from('matches')
    .update({
      match_date: scheduleData.match_date,
      match_time: scheduleData.match_time,
      // Also update new fields if available
      scheduled_datetime: scheduleData.scheduled_datetime || null,
      scheduler_timezone: scheduleData.scheduler_timezone || null
    })
    .eq('id', matchId)
    .select()
    .single();

    if (error) throw error;
    return { success: true, data };
  }, 'updating match schedule');
};

export const updateJourneySchedule = async (tournamentId, journeyNumber, scheduleData, userId) => {
  return dbOperation(async () => {
    // Verify tournament management permissions
    await verifyTournamentManagement(userId, tournamentId);

    // Update all matches in the specified journey
    const { data, error } = await supabase
      .from('matches')
      .update({
        match_date: scheduleData.match_date,
        match_time: scheduleData.match_time
      })
      .eq('tournament_id', tournamentId)
      .eq('journey_number', journeyNumber)
      .select();

    if (error) throw error;
    return { success: true, data, updatedCount: data.length };
  }, 'updating journey schedule');
};

export const updateMatchVodUrls = async (matchId, vodData, userId) => {
  return dbOperation(async () => {
    // Get tournament ID from match to verify permissions
    const { data: matchData, error: tournamentError } = await supabase
      .from('matches')
      .select('tournament_id')
      .eq('id', matchId)
      .single();
    
    if (tournamentError) throw tournamentError;
    
    await verifyTournamentManagement(userId, matchData.tournament_id);

    const { data, error } = await supabase
    .from('matches')
    .update({
      team1_vod_url: vodData.team1_vod_url || null,
      team2_vod_url: vodData.team2_vod_url || null
    })
    .eq('id', matchId)
    .select()
    .single();

    if (error) throw error;
    return { success: true, data };
  }, 'updating match VOD URLs');
};

// Player-specific VOD submission function - allows team members to submit VOD for their team
export const submitTeamVodUrl = async (matchId, vodUrl, userId) => {
  return dbOperation(async () => {
    // Input validation
    if (!matchId || !userId) {
      throw new Error('Missing required parameters for VOD submission');
    }

    // Get match data to determine which team the user belongs to
    const { data: matchData, error: matchError } = await supabase
      .from('matches')
      .select(`
        id,
        team1_id,
        team2_id,
        team1_vod_url,
        team2_vod_url,
        tournament_id
      `)
      .eq('id', matchId)
      .single();
    
    if (matchError) throw matchError;
    if (!matchData) throw new Error('Match not found');

    // Check which team the user belongs to
    const { data: userTeams, error: teamError } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('user_id', userId)
      .in('team_id', [matchData.team1_id, matchData.team2_id]);
    
    if (teamError) throw teamError;
    if (!userTeams || userTeams.length === 0) {
      throw new Error('You must be a member of one of the participating teams to submit VOD');
    }

    // Determine which VOD URL field to update based on team membership
    const userTeamId = userTeams[0].team_id;
    let updateData = {};
    
    if (userTeamId === matchData.team1_id) {
      updateData.team1_vod_url = vodUrl || null;
    } else if (userTeamId === matchData.team2_id) {
      updateData.team2_vod_url = vodUrl || null;
    } else {
      throw new Error('Invalid team membership for this match');
    }

    // Update the match with the VOD URL
    const { data, error } = await supabase
      .from('matches')
      .update(updateData)
      .eq('id', matchId)
      .select()
      .single();

    if (error) throw error;
    
    return { 
      success: true, 
      data,
      teamId: userTeamId,
      isTeam1: userTeamId === matchData.team1_id
    };
  }, 'submitting team VOD URL');
};

export const recordMatchResult = async (matchId, resultData, userId) => {
  return dbOperation(async () => {
    // Basic input validation
    if (!matchId || !resultData || !userId) {
      throw new Error('Missing required parameters for match result recording');
    }
    
    if (typeof resultData.team1_score !== 'number' || typeof resultData.team2_score !== 'number') {
      throw new Error('Team scores must be valid numbers');
    }
    
    if (resultData.team1_score < 0 || resultData.team2_score < 0) {
      throw new Error('Team scores cannot be negative');
    }

    // Deduplicate player stats at the frontend level to prevent constraint violations
    let deduplicatedPlayerStats = [];
    if (resultData.playerStats && resultData.playerStats.length > 0) {
      const playerStatsMap = new Map();
      resultData.playerStats.forEach(stat => {
        if (stat.player_id) {
          // Keep the last occurrence of each player_id
          playerStatsMap.set(stat.player_id, {
            player_id: stat.player_id,
            team_id: stat.team_id,
            goals: stat.goals || 0,
            assists: stat.assists || 0,
            passes: stat.passes || 0,
            interceptions: stat.interceptions || 0,
            saves: stat.saves || 0
          });
        }
      });
      deduplicatedPlayerStats = Array.from(playerStatsMap.values());
    }

    // Prepare data for RPC function
    const rpcData = {
      team1_score: resultData.team1_score,
      team2_score: resultData.team2_score,
      team1_vod_url: resultData.team1_vod_url || null,
      team2_vod_url: resultData.team2_vod_url || null,
      mvp_player_id: resultData.mvpPlayerId || null,
      player_stats: deduplicatedPlayerStats
    };

    // Call the secure RPC function on the server
    const { data, error } = await supabase.rpc('update_match_result', {
      p_match_id: matchId,
      p_result_data: rpcData,
      p_user_id: userId
    });

    if (error) {
      throw new Error(error.message || 'Failed to update match result');
    }

    // Return the result from the RPC function
    return data || { success: true };
  }, 'recording match result');
};

// ========================================
// 🎯 BO3/BO5 SERIES FUNCTIONS  
// ========================================

// Get series status and progress
export const getSeriesStatus = async (seriesId) => {
  return dbOperation(async () => {
    // Input validation
    if (!seriesId) {
      throw new Error('Series ID is required');
    }
    
    const { data: matches, error } = await supabase
      .from('matches')
      .select(`
        *, 
        team1:team1_id(id, name, logo),
        team2:team2_id(id, name, logo),
        winner:winner_id(id, name, logo)
      `)
      .eq('series_id', seriesId)
      .order('game_number');

    if (error) throw error;
    
    if (!matches || matches.length === 0) {
      throw new Error('No matches found for series');
    }

    const firstMatch = matches[0];
    const completedGames = matches.filter(m => m.status === 'completed');
    const team1Wins = completedGames.filter(m => m.winner_id === firstMatch.team1_id).length;
    const team2Wins = completedGames.filter(m => m.winner_id === firstMatch.team2_id).length;

    return {
      success: true,
      data: {
        series_id: seriesId,
        series_type: firstMatch.series_type,
        games_to_win: firstMatch.games_to_win,
        team1: firstMatch.team1,
        team2: firstMatch.team2,
        team1_wins: team1Wins,
        team2_wins: team2Wins,
        series_complete: firstMatch.series_complete,
        series_winner_id: firstMatch.series_winner_id,
        games: matches,
        current_game: matches.find(m => m.status === 'scheduled'),
        next_game: matches.find(m => m.status === 'pending')
      }
    };
  }, 'getting series status');
};

// ========================================
// 🎯 LEGACY BO3/BO5 FUNCTIONS (TO BE REMOVED)
// ========================================

/**
 * Get all games for a series (for BO3/BO5) - Updated for unified structure
 */
export const getMatchGames = async (matchId) => {
  return dbOperation(async () => {
    // First try to get games by series_id (assume matchId is series_id)
    let { data, error } = await supabase
      .from('matches')
      .select('*')
      .eq('series_id', matchId)
      .order('game_number');

    // If no results, try to get series_id from match and then get games
    if (!error && (!data || data.length === 0)) {
      const { data: matchData, error: matchError } = await supabase
        .from('matches')
        .select('series_id')
        .eq('id', matchId)
        .single();

      if (!matchError && matchData) {
        const { data: gamesData, error: gamesError } = await supabase
          .from('matches')
          .select('*')
          .eq('series_id', matchData.series_id)
          .order('game_number');
        
        if (gamesError) throw gamesError;
        data = gamesData;
      }
    }

    if (error) throw error;
    return { success: true, data };
  }, 'fetching match games');
};

/**
 * Create a new game within a series - Updated for unified structure
 */
export const createMatchGame = async (matchId, gameNumber, userId) => {
  return dbOperation(async () => {
    // Get series info from the match
    const { data: matchData, error: matchError } = await supabase
      .from('matches')
      .select('tournament_id, series_id, series_type, games_to_win, team1_id, team2_id, round_number, match_number')
      .eq('id', matchId)
      .single();
    
    if (matchError) throw matchError;
    
    await verifyTeamMembership(userId, matchData.team1_id, matchData.team2_id);

    // Create new game in the unified matches table
    const { data, error } = await supabase
      .from('matches')
      .insert({
        tournament_id: matchData.tournament_id,
        series_id: matchData.series_id,
        game_number: gameNumber,
        series_type: matchData.series_type,
        games_to_win: matchData.games_to_win,
        team1_id: matchData.team1_id,
        team2_id: matchData.team2_id,
        round_number: matchData.round_number,
        match_number: matchData.match_number,
        status: 'scheduled'
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  }, 'creating match game');
};

/**
 * Record result for a specific game in BO3/BO5 series - Updated for unified structure
 */
export const recordGameResult = async (gameId, gameData, userId) => {
  return dbOperation(async () => {
    console.log('🎮 recordGameResult called:', { gameId, userId });
    
    // Get tournament ID from game to verify permissions
    const { data: gameDetails, error: gameError } = await supabase
      .from('matches')
      .select('tournament_id, team1_id, team2_id, series_id, game_number')
      .eq('id', gameId)
      .single();
    
    if (gameError) {
      console.error('❌ Game details error:', gameError);
      throw gameError;
    }
    
    console.log('📋 Game details:', gameDetails);
    
    // Check if user has tournament management permissions OR is a team member
    try {
      await verifyTournamentManagement(userId, gameDetails.tournament_id);
    } catch (managementError) {
      console.log('❌ No tournament management access, checking team membership...');
      await verifyTeamMembership(userId, gameDetails.team1_id, gameDetails.team2_id);
    }

    const { team1_score, team2_score, playerStats, mvpPlayerId } = gameData;

    // Determine game winner
    let gameWinnerId = null;
    if (team1_score > team2_score) {
      gameWinnerId = gameDetails.team1_id;
    } else if (team2_score > team1_score) {
      gameWinnerId = gameDetails.team2_id;
    }

    // Update the game result directly (since the game already exists in unified structure)
    const updateData = {
      team1_score,
      team2_score,
      winner_id: gameWinnerId,
      status: 'completed'
    };
    
    // Include MVP player ID if provided (direct MVP selection)
    if (mvpPlayerId !== undefined) {
      updateData.mvp_player_id = mvpPlayerId || null;
    }
    
    const { data: updatedGame, error: updateError } = await supabase
      .from('matches')
      .update(updateData)
      .eq('id', gameId)
      .select()
      .single();

    if (updateError) throw updateError;

    // Record player stats for this game using safe upsert function
    if (playerStats?.length > 0) {
      // Deduplicate player stats by keeping the last occurrence of each player
      const deduplicatedStats = playerStats.reduce((acc, stat) => {
        // Keep the last occurrence of each player_id
        acc[stat.player_id] = {
          player_id: stat.player_id,
          team_id: stat.team_id,
          goals: stat.goals || 0,
          assists: stat.assists || 0,
          passes: stat.passes || 0,
          interceptions: stat.interceptions || 0,
          saves: stat.saves || 0
        };
        return acc;
      }, {});

      const uniqueStats = Object.values(deduplicatedStats);
      
      // Use the safe upsert function from the database
      const { error: statsError } = await supabase
        .rpc('upsert_player_match_stats', {
          p_match_id: gameId,
          p_player_stats: uniqueStats
        });

      if (statsError) throw statsError;
    } else {
      // If no player stats provided, remove existing stats for this match
      await supabase
        .from('player_match_stats')
        .delete()
        .eq('match_id', gameId);
    }

    // The trigger will automatically update the series status and advance winners
    // Including automatic Swiss tournament round advancement
    
    // MVP is now selected directly during game result submission (no separate voting)
    
    // Return the updated game
    return { success: true, data: updatedGame };
  }, 'recording game result');
};

/**
 * Get detailed match with games for BO3/BO5
 */
export const getMatchWithGames = async (matchId) => {
  return dbOperation(async () => {
    const [matchResult, gamesResult] = await Promise.all([
      getMatchDetails(matchId),
      getMatchGames(matchId)
    ]);

    if (!matchResult.success) throw new Error(matchResult.error);
    if (!gamesResult.success) throw new Error(gamesResult.error);

    return {
      success: true,
      data: {
        ...matchResult.data,
        games: gamesResult.data
      }
    };
  }, 'fetching match with games');
};

/**
 * Create next game in BO3/BO5 series if needed
 */
export const createNextGameIfNeeded = async (matchId, userId) => {
  return dbOperation(async () => {
    // Get match info to verify permissions
    const { data: matchData, error: tournamentError } = await supabase
      .from('matches')
      .select('tournament_id, team1_id, team2_id')
      .eq('id', matchId)
      .single();
    
    if (tournamentError) throw tournamentError;
    
    await verifyTeamMembership(userId, matchData.team1_id, matchData.team2_id);

    const { data: match, error: matchError } = await supabase
    .from('matches')
    .select('series_type, games_to_win, team1_games_won, team2_games_won, status')
    .eq('id', matchId)
    .single();

    if (matchError) throw matchError;

    // Only create next game if series is not complete
    if (match.status === 'completed') {
      return { success: true, message: 'Series already completed' };
    }

    // Check if either team has won the series
    if (match.team1_games_won >= match.games_to_win || match.team2_games_won >= match.games_to_win) {
      return { success: true, message: 'Series completed' };
    }

    // Get current games
    const gamesResult = await getMatchGames(matchId);
    if (!gamesResult.success) throw new Error(gamesResult.error);

    const games = gamesResult.data;
    const nextGameNumber = games.length + 1;
    const maxGames = match.series_type === 'bo3' ? 3 : 5;

    // Only create if we haven't reached max games
    if (nextGameNumber <= maxGames) {
      const result = await createMatchGame(matchId, nextGameNumber, userId);
      return result;
    }

    return { success: true, message: 'No more games needed' };
  }, 'creating next game if needed');
};

// ========================================
// 📊 LEADERBOARD FUNCTIONS
// ========================================

export const getTopGoalScorers = async (limit = 10) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .rpc('get_player_leaderboards')
    .gte('total_goals', 1)
    .order('total_goals', { ascending: false })
    .order('total_assists', { ascending: false })
    .limit(limit);

    if (error) throw error;
    return { success: true, data };
  }, 'fetching top goal scorers');
};

export const getTopAssistLeaders = async (limit = 10) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .rpc('get_player_leaderboards')
    .gte('total_assists', 1)
    .order('total_assists', { ascending: false })
    .order('total_goals', { ascending: false })
    .limit(limit);

    if (error) throw error;
    return { success: true, data };
  }, 'fetching top assist leaders');
};

/**
 * Get top pass leaders
 */
export const getTopPassLeaders = async (limit = 10) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .rpc('get_player_leaderboards')
    .gte('total_passes', 1)
    .order('total_passes', { ascending: false })
    .order('total_goals', { ascending: false })
    .limit(limit);

    if (error) throw error;
    return { success: true, data };
  }, 'fetching top pass leaders');
};

/**
 * Get top interception leaders
 */
export const getTopInterceptionLeaders = async (limit = 10) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .rpc('get_player_leaderboards')
    .gte('total_interceptions', 1)
    .order('total_interceptions', { ascending: false })
    .order('total_goals', { ascending: false })
    .limit(limit);

    if (error) throw error;
    return { success: true, data };
  }, 'fetching top interception leaders');
};

/**
 * Get top save leaders
 */
export const getTopSaveLeaders = async (limit = 10) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .rpc('get_player_leaderboards')
    .gte('total_saves', 1)
    .order('total_saves', { ascending: false })
    .order('total_goals', { ascending: false })
    .limit(limit);

    if (error) throw error;
    return { success: true, data };
  }, 'fetching top save leaders');
};

export const getTopTeams = async (limit = 10) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
    .rpc('get_team_leaderboards')
    .limit(limit);

    if (error) throw error;
    return { success: true, data };
  }, 'fetching top teams');
};

export const getAllTeamsWithStats = async () => {
  return dbOperation(async () => {
    // Get all active teams with member info
    const { data: allTeams, error: teamsError } = await supabase
      .from('teams')
      .select(`
        *,
        owner:users!teams_owner_id_fkey(username, display_name, avatar_url),
        members:team_members(
          user:users(id, username, display_name, avatar_url),
          role,
          joined_at
        )
      `)
      .eq('is_active', true)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (teamsError) throw teamsError;

    // Get team stats from leaderboards
    const { data: teamStats, error: statsError } = await supabase
      .rpc('get_team_leaderboards');

    if (statsError) throw statsError;

    // Create a stats lookup map
    const statsMap = new Map();
    (teamStats || []).forEach(stat => {
      statsMap.set(stat.team_id, stat);
    });

    // Transform and merge data
    const transformedTeams = (allTeams || []).map(team => {
      const stats = statsMap.get(team.id) || {
        match_wins: 0,
        match_losses: 0,
        tournaments_won: 0,
        elo_rating: 1000,
        total_team_goals: 0,
        total_matches: 0
      };

      return {
        id: team.id,
        name: team.name,
        description: team.description || '',
        logo: team.logo || '🏆',
        country_code: team.country_code,
        recruitment_status: team.recruitment_status,
        owner: team.owner?.username || team.owner?.display_name || 'Owner',
        ownerId: team.owner_id,
        ownerName: team.owner?.username || team.owner?.display_name,
        ownerAvatarUrl: team.owner?.avatar_url,
        members: team.members || [],
        stats: {
          wins: stats.match_wins || 0,
          losses: stats.match_losses || 0,
          draws: 0, // Not currently tracked but included for consistency
          tournamentsWon: stats.tournaments_won || 0,
          rank: stats.elo_rating || 1000,
          totalGoals: stats.total_team_goals || 0,
          totalMatches: stats.total_matches || 0
        },
        createdAt: team.created_at,
        isActive: team.is_active
      };
    });

    return { success: true, data: transformedTeams };
  }, 'fetching all teams with stats');
};

export const getLeaderboardStats = async () => {
  return dbOperation(async () => {
    const [playerResult, teamResult] = await Promise.all([
      supabase.rpc('get_player_leaderboards'),
      supabase.rpc('get_team_leaderboards')
    ]);

    if (playerResult.error) throw playerResult.error;
    if (teamResult.error) throw teamResult.error;

    return {
      success: true,
      data: {
        players: playerResult.data,
        teams: teamResult.data,
        topGoalScorers: playerResult.data
        .filter(p => p.total_goals > 0)
        .sort((a, b) => b.total_goals - a.total_goals || b.total_assists - a.total_assists)
        .slice(0, 10),
        topAssistLeaders: playerResult.data
        .filter(p => p.total_assists > 0)
        .sort((a, b) => b.total_assists - a.total_assists || b.total_goals - a.total_goals)
        .slice(0, 10),
        topTeams: teamResult.data.slice(0, 10)
      }
    };
  }, 'fetching leaderboard stats');
};

// ========================================
// 🏆 TOURNAMENT-SPECIFIC LEADERBOARDS  
// ========================================

/**
 * Get player leaderboards for a specific tournament
 */
export const getTournamentPlayerStats = async (tournamentId, limit = 10) => {
  return dbOperation(async () => {
    // Get player stats aggregated from matches within the tournament
    const { data, error } = await supabase
      .from('player_match_stats')
      .select(`
        player_id,
        player:users(id, username, display_name, avatar_url, country_code),
        team_id,
        team:teams(id, name, logo, country_code),
        goals,
        assists,
        passes,
        interceptions,
        saves,
        match:matches!inner(tournament_id)
      `)
      .eq('match.tournament_id', tournamentId);

    if (error) throw error;

    // Aggregate stats by player
    const playerStatsMap = {};
    
    data.forEach(stat => {
      const playerId = stat.player_id;
      if (!playerStatsMap[playerId]) {
        playerStatsMap[playerId] = {
          player_id: playerId,
          player: stat.player,
          team_id: stat.team_id,
          team: stat.team,
          total_goals: 0,
          total_assists: 0,
          total_passes: 0,
          total_interceptions: 0,
          total_saves: 0,
          matches_played: 0
        };
      }
      
      // Update team info if we have better/more recent team data
      if (stat.team && stat.team_id && (!playerStatsMap[playerId].team || !playerStatsMap[playerId].team.logo)) {
        playerStatsMap[playerId].team_id = stat.team_id;
        playerStatsMap[playerId].team = stat.team;
      }
      
      playerStatsMap[playerId].total_goals += stat.goals || 0;
      playerStatsMap[playerId].total_assists += stat.assists || 0;
      playerStatsMap[playerId].total_passes += stat.passes || 0;
      playerStatsMap[playerId].total_interceptions += stat.interceptions || 0;
      playerStatsMap[playerId].total_saves += stat.saves || 0;
      playerStatsMap[playerId].matches_played += 1;
    });

    const aggregatedStats = Object.values(playerStatsMap);
    
    return { success: true, data: aggregatedStats };
  }, 'fetching tournament player stats');
};

/**
 * Get team leaderboards for a specific tournament  
 */
export const getTournamentTeamStats = async (tournamentId, limit = 10) => {
  return dbOperation(async () => {
    // Get team registrations for this tournament
    const { data: registrations, error: regError } = await supabase
      .from('tournament_registrations')
      .select(`
        team_id,
        team:teams(id, name, logo, country_code),
        tournament_id
      `)
      .eq('tournament_id', tournamentId);

    if (regError) throw regError;

    // Get match results for teams in this tournament
    const teamIds = registrations.map(reg => reg.team_id);
    
    if (teamIds.length === 0) {
      return { success: true, data: [] };
    }

    const { data: matches, error: matchError } = await supabase
      .from('matches')
      .select(`
        id,
        team1_id,
        team2_id,
        team1_score,
        team2_score,
        winner_id,
        status
      `)
      .eq('tournament_id', tournamentId)
      .eq('status', 'completed')
      .or(`team1_id.in.(${teamIds.join(',')}),team2_id.in.(${teamIds.join(',')})`);

    if (matchError) throw matchError;

    // Calculate team statistics
    const teamStatsMap = {};
    
    // Initialize stats for all registered teams
    registrations.forEach(reg => {
      teamStatsMap[reg.team_id] = {
        team_id: reg.team_id,
        team: reg.team,
        match_wins: 0,
        match_losses: 0,
        total_team_goals: 0,
        total_goals_against: 0,
        matches_played: 0
      };
    });

    // Calculate stats from matches
    matches.forEach(match => {
      const team1Id = match.team1_id;
      const team2Id = match.team2_id;
      
      if (teamStatsMap[team1Id]) {
        teamStatsMap[team1Id].matches_played += 1;
        teamStatsMap[team1Id].total_team_goals += match.team1_score || 0;
        teamStatsMap[team1Id].total_goals_against += match.team2_score || 0;
        
        if (match.winner_id === team1Id) {
          teamStatsMap[team1Id].match_wins += 1;
        } else if (match.winner_id === team2Id) {
          teamStatsMap[team1Id].match_losses += 1;
        }
      }
      
      if (teamStatsMap[team2Id]) {
        teamStatsMap[team2Id].matches_played += 1;
        teamStatsMap[team2Id].total_team_goals += match.team2_score || 0;
        teamStatsMap[team2Id].total_goals_against += match.team1_score || 0;
        
        if (match.winner_id === team2Id) {
          teamStatsMap[team2Id].match_wins += 1;
        } else if (match.winner_id === team1Id) {
          teamStatsMap[team2Id].match_losses += 1;
        }
      }
    });

    // Convert to array and sort by performance
    const teamStats = Object.values(teamStatsMap)
      .sort((a, b) => {
        // Primary sort: match wins (descending)
        if (b.match_wins !== a.match_wins) {
          return b.match_wins - a.match_wins;
        }
        
        // Secondary sort: goal difference (goals for - goals against)
        const aGoalDiff = a.total_team_goals - a.total_goals_against;
        const bGoalDiff = b.total_team_goals - b.total_goals_against;
        if (bGoalDiff !== aGoalDiff) {
          return bGoalDiff - aGoalDiff;
        }
        
        // Tertiary sort: total goals scored (descending)
        if (b.total_team_goals !== a.total_team_goals) {
          return b.total_team_goals - a.total_team_goals;
        }
        
        // Final sort: fewer losses (ascending)
        return a.match_losses - b.match_losses;
      })
      .slice(0, limit); // Apply limit after sorting
    
    return { success: true, data: teamStats };
  }, 'fetching tournament team stats');
};

/**
 * Get comprehensive tournament leaderboard stats
 */
export const getTournamentLeaderboardStats = async (tournamentId) => {
  return dbOperation(async () => {
    const [playerResult, teamResult] = await Promise.all([
      getTournamentPlayerStats(tournamentId),
      getTournamentTeamStats(tournamentId)
    ]);

    if (!playerResult.success) throw new Error(playerResult.error);
    if (!teamResult.success) throw new Error(teamResult.error);

    const players = playerResult.data;
    const teams = teamResult.data;

    return {
      success: true,
      data: {
        players,
        teams,
        topGoalScorers: players
          .filter(p => p.total_goals > 0)
          .sort((a, b) => b.total_goals - a.total_goals || b.total_assists - a.total_assists)
          .slice(0, 10),
        topAssistLeaders: players
          .filter(p => p.total_assists > 0)
          .sort((a, b) => b.total_assists - a.total_assists || b.total_goals - a.total_goals)
          .slice(0, 10),
        topPassLeaders: players
          .filter(p => p.total_passes > 0)
          .sort((a, b) => b.total_passes - a.total_passes)
          .slice(0, 10),
        topInterceptionLeaders: players
          .filter(p => p.total_interceptions > 0)
          .sort((a, b) => b.total_interceptions - a.total_interceptions)
          .slice(0, 10),
        topSaveLeaders: players
          .filter(p => p.total_saves > 0)
          .sort((a, b) => b.total_saves - a.total_saves)
          .slice(0, 10),
        topTeams: teams
          .sort((a, b) => b.match_wins - a.match_wins || (b.total_team_goals - b.total_goals_against) - (a.total_team_goals - a.total_goals_against))
          .slice(0, 10)
      }
    };
  }, 'fetching tournament leaderboard stats');
};

// ========================================
// 📤 TOURNAMENT MATCH SUBMISSIONS
// ========================================

/**
 * Submit tournament match result for approval
 * Players/teams submit scores with screenshot evidence for tournament matches
 */
export const submitTournamentMatchResult = async (matchId, teamId, submissionData) => {
  return dbOperation(async () => {
    
    const { data, error } = await supabase
      .rpc('submit_tournament_match_result', {
        p_match_id: matchId,
        p_team_id: teamId,
        p_match_data: submissionData.matchData,
        p_submission_notes: submissionData.notes || null
      });

    if (error) {
      console.error('❌ RPC error:', error);
      throw error;
    }
    
    if (data && !data.success) {
      console.error('❌ RPC returned failure:', data);
      throw new Error(data.error || 'Tournament submission failed');
    }
    
    // Emit real-time event for immediate UI updates (like scrims)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tournamentActionUpdate', {
        detail: {
          type: 'submission',
          matchId: matchId,
          teamId: teamId,
          action: 'submit'
        }
      }));
    }
    
    return { success: true, data };
  }, 'submitting tournament match result');
};

/**
 * Approve tournament match submission (enhanced for dual submissions)
 * Tournament owners can approve submitted match results
 */
export const approveTournamentSubmission = async (submissionId, selectedSubmissionId = null, reviewNotes = null) => {
  return dbOperation(async () => {
    
    const { data, error } = await supabase
      .rpc('approve_tournament_match_submission', {
        p_submission_id: submissionId,
        p_selected_submission_id: selectedSubmissionId,
        p_review_notes: reviewNotes
      });

    if (error) {
      console.error('❌ Approval RPC error:', error);
      throw error;
    }
    
    if (data && !data.success) {
      console.error('❌ Approval RPC returned failure:', data);
      throw new Error(data.error || 'Tournament approval failed');
    }
    
    // Emit real-time event for immediate UI updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tournamentActionUpdate', {
        detail: {
          type: 'approval',
          submissionId: submissionId,
          matchId: data.match_id,
          action: 'approve'
        }
      }));
    }
    
    return { success: true, data };
  }, 'approving tournament submission');
};

/**
 * Reject tournament match submission (enhanced for dual submissions)
 * Tournament owners can reject submitted match results
 */
export const rejectTournamentSubmission = async (submissionId, reviewNotes) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('reject_tournament_match_submission', {
        p_submission_id: submissionId,
        p_review_notes: reviewNotes
      });

    if (error) throw error;
    
    // Emit real-time event for immediate UI updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tournamentActionUpdate', {
        detail: {
          type: 'rejection',
          submissionId: submissionId,
          matchId: data.match_id,
          action: 'reject'
        }
      }));
    }
    
    return { success: true, data };
  }, 'rejecting tournament submission');
};

/**
 * Get pending tournament submissions for tournament owners
 * Returns all submissions awaiting approval for tournaments they own
 */
export const getPendingTournamentSubmissions = async (userId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('get_pending_tournament_submissions', {
        p_user_id: userId
      });

    if (error) throw error;
    
    // Transform data to maintain backward compatibility with frontend
    const transformedData = (data || []).map(item => ({
      id: item.out_id,
      match_id: item.out_match_id,
      tournament_id: item.out_tournament_id,
      tournament_name: item.out_tournament_name,
      team1_id: item.out_team1_id,
      team2_id: item.out_team2_id,
      team1_name: item.out_team1_name,
      team2_name: item.out_team2_name,
      submission_status: item.out_submission_status,
      submissions_match: item.out_submissions_match,
      auto_approvable: item.out_auto_approvable,
      team1_submission_id: item.out_team1_submission_id,
      team2_submission_id: item.out_team2_submission_id,
      team1_submission_data: item.out_team1_submission_data,
      team2_submission_data: item.out_team2_submission_data,
      team1_submitted_by: item.out_team1_submitted_by,
      team2_submitted_by: item.out_team2_submitted_by,
      team1_submitted_at: item.out_team1_submitted_at,
      team2_submitted_at: item.out_team2_submitted_at,
      created_at: item.out_created_at
    }));
    
    return { success: true, data: transformedData };
  }, 'fetching pending tournament submissions');
};

/**
 * Get tournament match submission history
 * Returns all submissions for a specific match
 */
export const getTournamentMatchSubmissions = async (matchId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .from('tournament_match_actions')
      .select(`
        *,
        submitted_by_user:users!tournament_match_actions_submitted_by_fkey (
          id,
          username,
          display_name,
          avatar_url
        ),
        reviewed_by_user:users!tournament_match_actions_reviewed_by_fkey (
          id,
          username,
          display_name,
          avatar_url
        ),
        team:teams (
          id,
          name,
          logo
        ),
        match:matches (
          id,
          team1_id,
          team2_id,
          status,
          tournament:tournaments (
            id,
            name,
            format
          )
        )
      `)
      .eq('match_id', matchId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  }, 'fetching tournament match submissions');
};

/**
 * Check if user can submit for tournament match
 * Verifies team membership and submission permissions
 */
export const canSubmitTournamentResult = async (matchId, userId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .from('matches')
      .select(`
        id,
        tournament_id,
        team1_id,
        team2_id,
        status,
        submission_status,
        tournament:tournaments (
          id,
          status,
          format
        ),
        team1:teams!matches_team1_id_fkey (
          id,
          members:team_members!team_members_team_id_fkey (
            user_id,
            role
          )
        ),
        team2:teams!matches_team2_id_fkey (
          id,
          members:team_members!team_members_team_id_fkey (
            user_id,
            role
          )
        )
      `)
      .eq('id', matchId)
      .single();

    if (error) throw error;
    
    const match = data;
    
    // Check if match is in correct status for submissions
    if (!['scheduled', 'in_progress'].includes(match.status)) {
      console.log('Match status check failed:', match.status, 'for match:', matchId);
      return { 
        success: false, 
        canSubmit: false, 
        reason: `Match is not available for submissions (status: ${match.status})` 
      };
    }
    
    // Check if tournament allows submissions (based on CLAUDE.md valid tournament statuses)
    if (!['open', 'in_progress', 'running', 'playoffs', 'knockout', 'group_stage', 'live'].includes(match.tournament.status)) {
      return { 
        success: false, 
        canSubmit: false, 
        reason: 'Tournament is not accepting submissions' 
      };
    }
    
    // Check if user is a member of either team
    const isTeam1Member = match.team1.members.some(
      member => member.user_id === userId
    );
    const isTeam2Member = match.team2.members.some(
      member => member.user_id === userId
    );
    
    if (!isTeam1Member && !isTeam2Member) {
      return { 
        success: false, 
        canSubmit: false, 
        reason: 'You are not a member of either team' 
      };
    }
    
    const userTeamId = isTeam1Member ? match.team1_id : match.team2_id;
    
    // Check if team has already submitted results for this match
    const { data: existingSubmissions, error: submissionError } = await supabase
      .from('tournament_match_actions')
      .select('id, action_type, match_id, team_id')
      .eq('match_id', matchId)
      .eq('team_id', userTeamId)
      .eq('action_type', 'pending_approval');
    
    if (submissionError) {
      console.warn('Error checking existing submissions:', submissionError);
      // Continue without blocking if we can't check - backend will handle it
    }
    
    if (existingSubmissions && existingSubmissions.length > 0) {
      console.log('🚫 Match', matchId, 'has pending submission, blocking new submission. Submissions:', existingSubmissions);
      return { 
        success: false, 
        canSubmit: false, 
        reason: 'Your team has already submitted results for this match and is awaiting approval',
        hasExistingSubmission: true
      };
    }
    
    return { 
      success: true, 
      canSubmit: true, 
      userTeamId,
      match: match
    };
  }, 'checking tournament submission permissions');
};

/**
 * Enhanced batch check submission permissions for multiple matches (dual submission support)
 * Uses the new backend function that provides detailed submission status
 */
export const batchCheckTournamentSubmissionPermissions = async (matchIds, userId = null) => {
  return dbOperation(async () => {
    // Get current user ID if not provided
    let currentUserId = userId;
    if (!currentUserId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      currentUserId = user.id;
    }

    const { data, error } = await supabase
      .rpc('batch_check_tournament_submission_permissions', {
        p_match_ids: matchIds,
        p_user_id: currentUserId
      });

    if (error) throw error;
    
    // Transform the results to match expected format
    return {
      success: true,
      results: data.map(result => ({
        matchId: result.out_match_id,
        canSubmit: result.out_can_submit,
        hasTeamSubmission: result.out_has_team_submission,
        hasOtherTeamSubmission: result.out_has_other_team_submission,
        submissionStatus: result.out_submission_status, // 'none', 'partial', 'complete'
        reason: result.out_reason
      }))
    };
  }, 'batch checking tournament submission permissions');
};

/**
 * Legacy batch check submission permissions (kept for backward compatibility)
 */
export const batchCheckPermissions = async (matchIds, userId) => {
  if (!matchIds || matchIds.length === 0) {
    return { success: true, results: [] };
  }
  
  try {
    // Get all matches data in one query
    const { data: matches, error: matchError } = await supabase
      .from('matches')
      .select(`
        id,
        tournament_id,
        team1_id,
        team2_id,
        status,
        tournament:tournaments (
          id,
          status,
          format
        ),
        team1:teams!matches_team1_id_fkey (
          id,
          members:team_members!team_members_team_id_fkey (
            user_id,
            role
          )
        ),
        team2:teams!matches_team2_id_fkey (
          id,
          members:team_members!team_members_team_id_fkey (
            user_id,
            role
          )
        )
      `)
      .in('id', matchIds);

    if (matchError) throw matchError;

    // Get all existing submissions in one query
    const { data: existingSubmissions, error: submissionError } = await supabase
      .from('tournament_match_actions')
      .select('id, action_type, match_id, team_id')
      .in('match_id', matchIds)
      .eq('action_type', 'pending_approval');

    if (submissionError) {
      console.warn('Error checking existing submissions:', submissionError);
    }

    // Group submissions by match_id and team_id
    const submissionMap = new Map();
    (existingSubmissions || []).forEach(sub => {
      const key = `${sub.match_id}_${sub.team_id}`;
      submissionMap.set(key, sub);
    });

    // Process each match
    const results = matches.map(match => {
      // Check if match is in correct status for submissions
      if (!['scheduled', 'in_progress'].includes(match.status)) {
        return {
          matchId: match.id,
          success: false,
          canSubmit: false,
          reason: `Match is not available for submissions (status: ${match.status})`
        };
      }

      // Check if tournament allows submissions
      if (!['open', 'in_progress', 'running', 'playoffs', 'knockout', 'group_stage', 'live'].includes(match.tournament.status)) {
        return {
          matchId: match.id,
          success: false,
          canSubmit: false,
          reason: 'Tournament is not accepting submissions'
        };
      }

      // Check if user is a member of either team
      const isTeam1Member = match.team1.members.some(member => member.user_id === userId);
      const isTeam2Member = match.team2.members.some(member => member.user_id === userId);

      if (!isTeam1Member && !isTeam2Member) {
        return {
          matchId: match.id,
          success: false,
          canSubmit: false,
          reason: 'You are not a member of either team'
        };
      }

      const userTeamId = isTeam1Member ? match.team1_id : match.team2_id;
      const submissionKey = `${match.id}_${userTeamId}`;
      const hasExistingSubmission = submissionMap.has(submissionKey);

      if (hasExistingSubmission) {
        console.log('🚫 Match', match.id, 'has pending submission for team', userTeamId);
        return {
          matchId: match.id,
          success: false,
          canSubmit: false,
          reason: 'Your team has already submitted results for this match and is awaiting approval',
          hasExistingSubmission: true
        };
      }

      return {
        matchId: match.id,
        success: true,
        canSubmit: true,
        userTeamId,
        match: match
      };
    });

    return { success: true, results };
  } catch (error) {
    console.error('Error in batch permission check:', error);
    return { success: false, error: error.message };
  }
};

// ========================================
// 🗑️ TOURNAMENT DELETION
// ========================================

/**
 * Delete a tournament and all related data
 * WARNING: This is a destructive operation that will:
 * - Delete all matches and match games
 * - Delete all player match statistics
 * - Delete all team registrations
 * - Delete all group assignments and groups
 * - Delete the tournament itself
 */
export const deleteTournament = async (tournamentId, adminId) => {
  return dbOperation(async () => {
    console.log(`Starting tournament deletion process for ID: ${tournamentId}`);
    
    await verifyAdmin(adminId);
    console.log('Admin verification passed');

    // Try using the complete RPC function first (if it exists)
    try {
      const { data: result, error: rpcError } = await supabase
        .rpc('admin_delete_tournament_complete', {
          tournament_id_param: tournamentId,
          admin_id_param: adminId
        });

      if (rpcError) {
        console.log('RPC function not available, falling back to simple deletion');
        throw rpcError;
      }

      if (result && result.success) {
        console.log('Tournament deleted successfully via RPC!');
        console.log('Deletion summary:', result.deleted_counts);
        
        return {
          success: true,
          data: {
            tournamentId: result.tournament_id,
            tournamentName: result.tournament_name,
            wasStarted: result.was_started,
            deletedAt: result.deleted_at,
            deletionSummary: result.deleted_counts
          }
        };
      }
    } catch (rpcError) {
      console.log('RPC deletion failed, using fallback method:', rpcError.message);
    }

    // Fallback to simplified deletion
    return deleteTournamentSimple(tournamentId, adminId);
  }, 'deleting tournament');
};

// ========================================
// 📊 PLAYERS DATA
// ========================================

/**
 * Get players with server-side pagination, filtering, and sorting
 * @param {Object} options - Query options
 * @param {string} options.searchTerm - Search term for username/display_name
 * @param {string} options.countryCode - Filter by country code
 * @param {string} options.region - Filter by region (europe, northAmerica, etc.)
 * @param {string} options.position - Filter by position
 * @param {string} options.platform - Filter by platform
 * @param {string} options.clubStatus - Filter by club status (club, freeagent)
 * @param {string} options.sortBy - Sort field (goals, assists, mvps, etc.)
 * @param {string} options.sortOrder - Sort order (asc, desc)
 * @param {number} options.limit - Number of records per page (default: 12)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @returns {Promise<{success: boolean, data: Array, totalCount: number, error?: string}>}
 */
export const getPlayersPaginated = async ({
  searchTerm = null,
  countryCode = null,
  region = null,
  position = null,
  platform = null,
  clubStatus = null,
  sortBy = 'goals',
  sortOrder = 'desc',
  limit = 12,
  offset = 0,
  abortSignal = null // Support for request cancellation
} = {}) => {
  return deduplicatedRequest(
    'getPlayersPaginated',
    [searchTerm, countryCode, region, position, platform, clubStatus, sortBy, sortOrder, limit, offset],
    async () => {
      try {
        // Check if request was cancelled before making the call
        if (abortSignal?.aborted) {
          throw new Error('Request was cancelled');
        }

        // Call the new paginated RPC function
        const rpcPromise = supabase.rpc('get_players_paginated', {
          search_term: searchTerm,
          country_code_filter: countryCode,
          region_filter: region,
          position_filter: position,
          platform_filter: platform,
          club_status_filter: clubStatus,
          sort_by: sortBy,
          sort_order: sortOrder,
          page_limit: limit,
          page_offset: offset
        });

        // Wrap with cancellation support
        const { data: playersData, error: rpcError } = await Promise.race([
          rpcPromise,
          new Promise((_, reject) => {
            if (abortSignal) {
              abortSignal.addEventListener('abort', () => {
                reject(new Error('Request was cancelled'));
              });
            }
          })
        ]);

        if (rpcError) {
          console.error('Error fetching paginated players:', rpcError);
          throw rpcError;
        }

        if (!playersData || playersData.length === 0) {
          return { 
            success: true, 
            data: [], 
            totalCount: 0 
          };
        }

        // Extract total count from first record (all records have same total_count)
        const totalCount = playersData[0]?.total_count || 0;
        
        // Remove total_count from individual records to clean up the data
        const cleanedData = playersData.map(({ total_count, ...player }) => player);

        return {
          success: true,
          data: cleanedData,
          totalCount: parseInt(totalCount, 10)
        };
      } catch (error) {
        console.error('Error in getPlayersPaginated:', error);
        return {
          success: false,
          error: error.message || 'Failed to fetch players',
          data: [],
          totalCount: 0
        };
      }
    },
    { disableDeduplication: false } // Enable deduplication for read operations
  );
};

// ========================================
// 🎮 SCRIM LEAGUES FUNCTIONS
// ========================================

// ========================================
// 🛡️ ADMIN DISPUTE RESOLUTION
// ========================================

/**
 * Get team statistics in batch for multiple teams
 * Replaces individual get_team_leaderboards RPC calls
 */
export const getBatchTeamStats = async (teamIds = [], options = {}) => {
  return dbOperation(async () => {
    if (!teamIds || teamIds.length === 0) {
      return {};
    }

    const { data, error } = await supabase
      .rpc('get_team_leaderboards')
      .in('team_id', teamIds);

    if (error) throw error;
    
    // Convert array to map for easy lookup
    const statsMap = new Map();
    (data || []).forEach(stats => {
      statsMap.set(stats.team_id, stats);
    });
    
    return statsMap;
  }, 'getBatchTeamStats', options);
};

/**
 * Get individual team statistics (uses batch function for deduplication)
 */
export const getTeamStats = async (teamId, options = {}) => {
  const batchResults = await getBatchTeamStats([teamId], options);
  return batchResults.get(teamId) || null;
};

/**
 * Get player's match history
 */
/**
 * Get ALL player match history (cached by player + gameMode only)
 * Internal function for optimization - fetches all matches once
 */
const getPlayerScrimHistoryAll = async (playerId, limit = 50, gameMode = null) => {
  
  try {
    const result = await dbOperation(async () => {
      let query = supabase
        .from('scrim_match_players')
        .select(`
          *,
          match:scrim_matches(
            id,
            status,
            winner_team,
            team1_id,
            team2_id,
            team1_score,
            team2_score,
            team1_average_mmr,
            team2_average_mmr,
            game_mode,
            created_at,
            completed_at
          )
        `)
        .eq('player_id', playerId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (gameMode) {
        query = query.eq('match.game_mode', gameMode);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).filter(item => item.match != null).map(item => ({
        ...item,
        won: item.match.winner_team === item.team_number,
        opponent_avg_mmr: item.team_number === 1 
          ? item.match.team2_average_mmr 
          : item.match.team1_average_mmr
      }));
    }, 'getPlayerScrimHistoryAll', {
      cacheArgs: [playerId, limit, gameMode],
      cacheDuration: 30000 // 30 seconds cache for match history
    });

    // Check if dbOperation returned an error object instead of the data
    if (result && typeof result === 'object' && result.success === false) {
      console.error('getPlayerScrimHistoryAll error:', result.error);
      return [];
    }

    return result || [];
  } catch (error) {
    console.error('getPlayerScrimHistoryAll error:', error);
    return [];
  }
};

/**
 * Get player's match history filtered by team (optimized)
 * Uses cached ALL matches and filters in JavaScript for team-specific results
 */
export const getPlayerScrimHistory = async (playerId, limit = 20, gameMode = null, teamId = null) => {
  // Get all matches for this player/gameMode (cached)
  const allMatches = await getPlayerScrimHistoryAll(playerId, Math.max(limit, 50), gameMode);
  
  // Ensure we have a valid array
  if (!Array.isArray(allMatches)) {
    return [];
  }
  
  let filteredMatches = allMatches;
  
  // Filter by team if specified (JavaScript filtering, very fast)
  if (teamId) {
    filteredMatches = allMatches.filter(item => {
      const match = item.match;
      if (!match) return false; // Skip entries without match data
      // Player was on team 1 and team 1 matches the specified team
      if (item.team_number === 1 && match.team1_id === teamId) return true;
      // Player was on team 2 and team 2 matches the specified team  
      if (item.team_number === 2 && match.team2_id === teamId) return true;
      return false;
    });
  }
  
  // Apply limit after filtering
  return filteredMatches.slice(0, limit);
};

// ========================================
// 🏆 SIMPLIFIED MVP FUNCTIONS  
// ========================================

/**
 * Get global MVP leaderboard (simplified - no voting)
 */
export const getGlobalMVPLeaderboard = async (limit = 50) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('get_global_mvp_leaderboard', { limit_param: limit });

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  }, 'getting global MVP leaderboard');
};

// Owner user-ids for a set of teams (get_team_leaderboards only returns
// owner_name/avatar, not the id needed to link to a profile).
export const getTeamOwnerIds = async (teamIds = []) => {
  const ids = [...new Set((teamIds || []).filter(Boolean))];
  if (!ids.length) return { success: true, data: {} };
  return dbOperation(async () => {
    const { data, error } = await supabase.from('teams').select('id, owner_id').in('id', ids);
    if (error) throw error;
    const map = {};
    (data || []).forEach((r) => { if (r.id) map[r.id] = r.owner_id; });
    return { success: true, data: map };
  }, 'getting team owner ids', { cacheArgs: [ids.slice().sort().join(',')] });
};

// Real platform-wide totals for the homepage stats bar (head-only count queries,
// no rows transferred).
export const getPlatformStats = async () => {
  return dbOperation(async () => {
    const [players, teams, tournaments, live] = await Promise.all([
      supabase.from('users').select('id', { count: 'exact', head: true }),
      supabase.from('teams').select('id', { count: 'exact', head: true }).eq('is_active', true),
      supabase.from('tournaments').select('id', { count: 'exact', head: true }),
      // Live + upcoming = every tournament that isn't finished or hidden
      supabase.from('tournaments').select('id', { count: 'exact', head: true })
        .not('status', 'in', '(completed,hide)')
    ]);
    return {
      success: true,
      data: {
        players: players.count ?? 0,
        teams: teams.count ?? 0,
        tournaments: tournaments.count ?? 0,
        live: live.count ?? 0
      }
    };
  }, 'getting platform stats');
};

// Full player rows (computed stats incl. tournaments_won + total_mvps) for a set
// of player ids. Uses get_players_paginated (the same source /players uses),
// filtered server-side so only the requested rows are transferred.
export const getPlayersByIds = async (playerIds = []) => {
  const ids = [...new Set((playerIds || []).filter(Boolean))];
  if (!ids.length) return { success: true, data: [] };
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('get_players_paginated', { page_limit: 100000, page_offset: 0 })
      .in('player_id', ids);

    if (error) throw error;
    const cleaned = (data || []).map(({ total_count, ...p }) => p);
    return { success: true, data: cleaned };
  }, 'getting players by ids', { cacheArgs: [ids.slice().sort().join(',')] });
};

/**
 * Get tournament MVP leaderboard (simplified - no voting)
 */
export const getTournamentMVPLeaderboard = async (tournamentId, limit = 50) => {
  return dbOperation(async () => {
    if (!tournamentId) {
      throw new Error('Tournament ID is required');
    }

    const { data, error } = await supabase
      .rpc('get_tournament_mvp_leaderboard', { 
        tournament_id_param: tournamentId,
        limit_param: limit 
      });

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  }, 'getting tournament MVP leaderboard');
};

/**
 * Get player MVP stats (simplified - no voting)
 */
export const getPlayerMVPStats = async (playerId) => {
  return dbOperation(async () => {
    if (!playerId) {
      throw new Error('Player ID is required');
    }

    const { data, error } = await supabase
      .rpc('get_player_mvp_stats', { player_id_param: playerId });

    if (error) throw error;

    const playerStats = data?.[0];
    if (!playerStats) {
      return {
        success: true,
        data: {
          totalMVPs: 0,
          totalMatchesPlayed: 0,
          mvpPercentage: 0,
          recentMVPDate: null
        }
      };
    }

    return {
      success: true,
      data: {
        totalMVPs: parseInt(playerStats.total_mvps) || 0,
        totalMatchesPlayed: parseInt(playerStats.total_matches) || 0,
        mvpPercentage: parseFloat(playerStats.mvp_percentage) || 0,
        recentMVPDate: playerStats.recent_mvp_date || null
      }
    };
  }, 'getting player MVP stats');
};

/**
 * Get eligible MVP players for a match (for UI dropdowns)
 */
export const getMatchEligibleMVPPlayers = async (matchId) => {
  return dbOperation(async () => {
    if (!matchId) {
      throw new Error('Match ID is required');
    }

    // Try the new simplified function first
    let { data, error } = await supabase
      .rpc('get_match_eligible_mvp_players', { match_id_param: matchId });

    if (error) throw error;

    return {
      success: true,
      data: data || []
    };
  }, 'getting eligible MVP players');
};

// ========================================
// 📅 MATCH SCHEDULING
// ========================================

/**
 * Submit a schedule request for a match
 * Team captains/owners propose match schedules for opponent approval
 */
export const submitScheduleRequest = async (matchId, proposerTeamId, scheduleData) => {
  return dbOperation(async () => {
    
    const { data, error } = await supabase
      .rpc('submit_schedule_request', {
        p_match_id: matchId,
        p_proposer_team_id: proposerTeamId,
        p_proposed_date: scheduleData.proposedDate,
        p_proposed_time: scheduleData.proposedTime,
        p_timezone: scheduleData.timezone || 'Europe/Lisbon',
        p_proposer_notes: scheduleData.proposerNotes || null
      });

    if (error) {
      console.error('❌ Error submitting schedule request:', error);
      throw error;
    }

    return data;
  }, 'submitting schedule request');
};

/**
 * Respond to a schedule request (accept/reject/counter-propose)
 * Opponent team captains/owners respond to schedule proposals
 */
export const respondToScheduleRequest = async (requestId, userId, response, responseData = {}) => {
  return dbOperation(async () => {
    
    const { data, error } = await supabase
      .rpc('respond_to_schedule_request', {
        p_request_id: requestId,
        p_responder_user_id: userId,
        p_response: response, // 'accept', 'reject', 'counter_propose'
        p_response_notes: responseData.responseNotes || null,
        p_counter_date: responseData.counterDate || null,
        p_counter_time: responseData.counterTime || null
      });

    if (error) {
      console.error('❌ Error responding to schedule request:', error);
      throw error;
    }

    return data;
  }, 'responding to schedule request');
};

/**
 * Get schedule requests for a user
 * Returns all schedule requests where user is involved (proposer or responder)
 */
export const getScheduleRequestsForUser = async (userId) => {
  return dbOperation(async () => {
    
    const { data, error } = await supabase
      .rpc('get_schedule_requests_for_user', {
        p_user_id: userId
      });

    if (error) {
      console.error('❌ Error getting schedule requests:', error);
      throw error;
    }

    return {
      success: true,
      data: data || []
    };
  }, 'getting schedule requests for user');
};

/**
 * Get schedule notifications for a user
 * Returns unread schedule-related notifications
 */
export const getScheduleNotifications = async (userId) => {
  return dbOperation(async () => {
    
    const { data, error } = await supabase
      .from('schedule_notifications')
      .select(`
        id,
        schedule_request_id,
        notification_type,
        title,
        message,
        read,
        created_at,
        match_schedule_requests!inner(
          id,
          match_id,
          proposed_date,
          proposed_time,
          status,
          matches!inner(
            id,
            tournament_id,
            tournaments!inner(
              id,
              name
            ),
            team1:teams!team1_id(
              id,
              name
            ),
            team2:teams!team2_id(
              id,
              name
            )
          )
        )
      `)
      .eq('user_id', userId)
      .eq('read', false)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error getting schedule notifications:', error);
      throw error;
    }

    return {
      success: true,
      data: data || []
    };
  }, 'getting schedule notifications');
};

/**
 * Mark schedule notification as read
 */
export const markScheduleNotificationAsRead = async (notificationId) => {
  return dbOperation(async () => {
    
    const { error } = await supabase
      .from('schedule_notifications')
      .update({ read: true })
      .eq('id', notificationId);

    if (error) {
      console.error('❌ Error marking notification as read:', error);
      throw error;
    }

    return {
      success: true
    };
  }, 'marking schedule notification as read');
};

/**
 * Cancel a schedule request
 * Proposer can cancel their own pending requests
 */
export const cancelScheduleRequest = async (requestId, userId) => {
  return dbOperation(async () => {
    
    const { data, error } = await supabase
      .from('match_schedule_requests')
      .update({ 
        status: 'cancelled',
        updated_at: new Date().toISOString()
      })
      .eq('id', requestId)
      .eq('proposer_team_id', userId) // Only proposer can cancel
      .eq('status', 'pending') // Only pending requests can be cancelled
      .select('id, match_id');

    if (error) {
      console.error('❌ Error cancelling schedule request:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      throw new Error('Schedule request not found or cannot be cancelled');
    }

    // Update match status back to unscheduled
    const { error: matchError } = await supabase
      .from('matches')
      .update({ schedule_status: 'unscheduled' })
      .eq('id', data[0].match_id);

    if (matchError) {
      console.error('❌ Error updating match status:', matchError);
      // Don't throw here as the main operation succeeded
    }

    return {
      success: true,
      data: data[0]
    };
  }, 'cancelling schedule request');
};

/**
 * Approve a schedule request as tournament owner
 */
export const approveScheduleRequest = async (requestId, adminNotes = null) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('approve_schedule_request', {
        p_request_id: requestId,
        p_admin_user_id: (await supabase.auth.getUser()).data.user?.id,
        p_approval: 'approve',
        p_admin_notes: adminNotes
      });

    if (error) {
      throw error;
    }

    return data;
  }, 'approving schedule request');
};

/**
 * Reject a schedule request as tournament owner
 */
export const rejectScheduleRequest = async (requestId, adminNotes = null) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('approve_schedule_request', {
        p_request_id: requestId,
        p_admin_user_id: (await supabase.auth.getUser()).data.user?.id,
        p_approval: 'reject',
        p_admin_notes: adminNotes
      });

    if (error) {
      throw error;
    }

    return data;
  }, 'rejecting schedule request');
};

export const updateTournamentScheduleApprovalSetting = async (tournamentId, requireApproval, userId) => {
  return dbOperation(async () => {
    
    // First verify user is tournament owner
    const { data: tournament, error: fetchError } = await supabase
      .from('tournaments')
      .select('created_by')
      .eq('id', tournamentId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    if (tournament.created_by !== userId) {
      throw new Error('Only tournament owners can modify approval settings');
    }

    // Update the setting
    const { data, error } = await supabase
      .from('tournaments')
      .update({ require_schedule_approval: requireApproval })
      .eq('id', tournamentId)
      .select('id, name, require_schedule_approval');

    if (error) {
      throw error;
    }
    
    return {
      success: true,
      data: data[0]
    };
  }, 'updating tournament schedule approval setting');
};

/**
 * Get active schedule request for a match
 * Checks if there's an existing pending or counter-proposed request
 */
export const getActiveScheduleRequest = async (matchId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('get_active_schedule_request', {
        p_match_id: matchId
      });

    if (error) {
      console.error('❌ Error getting active schedule request:', error);
      throw error;
    }

    return data;
  }, 'getting active schedule request');
};

/**
 * Update an existing schedule request
 * Allows the proposing team to modify their pending request
 */
export const updateScheduleRequest = async (requestId, proposerTeamId, scheduleData) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('update_schedule_request', {
        p_request_id: requestId,
        p_proposer_team_id: proposerTeamId,
        p_proposed_date: scheduleData.proposedDate,
        p_proposed_time: scheduleData.proposedTime,
        p_timezone: scheduleData.timezone || 'Europe/Lisbon',
        p_proposer_notes: scheduleData.proposerNotes || null
      });

    if (error) {
      console.error('❌ Error updating schedule request:', error);
      throw error;
    }

    return data;
  }, 'updating schedule request');
};

/**
 * Check if user can manage schedule requests for a match
 * Returns permissions and team info
 */
export const canUserManageScheduleRequest = async (matchId, userId) => {
  return dbOperation(async () => {
    const { data, error } = await supabase
      .rpc('can_user_manage_schedule_request', {
        p_match_id: matchId,
        p_user_id: userId
      });

    if (error) {
      console.error('❌ Error checking schedule request permissions:', error);
      throw error;
    }

    return data;
  }, 'checking schedule request permissions');
};

// ========================================
// 🤝 PARTNERS MANAGEMENT
// ========================================
